import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  inject,
  ChangeDetectorRef,
  OnDestroy,
  NgZone,
  HostListener,
} from '@angular/core';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  Subject,
  catchError,
  debounceTime,
  firstValueFrom,
  of,
  switchMap,
  tap,
  forkJoin,
  Subscription,
  Observable,
  from,
  concatMap,
} from 'rxjs';
import { BarcodeScannerService } from '../../../core/services/barcode-scanner.service';
import {
  hasPurchasePrice,
  priceNotSetMessage,
} from '../../../core/utils/price-status.util';
import { PoService } from '../../po/po.service';
// NgSelectModule (not the bare NgSelectComponent) is what carries the
// ng-option-tmp / ng-label-tmp directives. Importing only the component makes
// a <ng-template ng-option-tmp> silently inert — it compiles without error and
// ng-select just falls back to rendering bindLabel.
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { SearchSelectComponent } from '../../../shared/components/search-select/search-select.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { GrnRevision, GrnService } from '../grn.service';
import {
  ReasonsService,
  TransferOutReason,
} from '../../transfer-out-list/reason.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { VendorService } from '../../vendors/vendor.service';
import { ProductsService } from '../../products/products.service';
import { ManufacturerService } from '../../manufacturers/manufacturers.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { finalize } from 'rxjs/operators';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';
import { OpenPrn, PrnService } from '../../prn-module/prn-moduel.service';
import { PermissionService } from '../../../core/services/permission.service';

/**
 * Roles allowed on the `prn/edit/:id` route (app.routes.ts). Mirrored here so
 * the "Open" link is hidden from users AuthGuard would bounce to /unauthorized.
 */
const PRN_EDIT_ROLES = [
  'Admin',
  'STORE_MANAGER',
  'CATEGORY_MANAGER',
  'PRN_TEAM',
  'ASM',
  'Asm',
  'AREA_MANAGER',
  'Area Manager',
];
import { AuthService } from '../../../auth/auth.service';
import { GstSlabService } from '../../gstslabs/gst-slab.service';
import { environment } from '../../../../environments/environment';
import {
  WarehouseSelectComponent,
  WarehouseOption,
} from '../../../shared/components/warehouse-select/warehouse-select.component';
import { QtyInputDirective } from '../../../shared/directives/qty-input.directive';
import { StickyColsDirective } from '../../../shared/directives/sticky-cols.directive';
import { consistentBasicCost, pricingForCost, pricingForLine } from '../../../shared/utils/basic-cost.util';
import { allowsDecimalQty } from '../../../shared/utils/quantity.util';
import { ScanTypeahead } from '../../../shared/utils/scan-typeahead.util';
import { allCodesOf, currentEanOf, variantHasCode, withMatchedEan } from '../../../core/utils/current-ean.util';
import { ignoredScanMessage, resolveScanCode } from '../../../core/utils/scan-code.util';

/** Business timezone — operations are India-only (mirrors the backend). */
const BUSINESS_TZ = 'Asia/Kolkata';

/** Today's calendar date in IST as yyyy-MM-dd. */
function istToday(): string {
  // 'en-CA' formats as YYYY-MM-DD with zero-padded parts.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** One row of the "Add products" dialog (see openProductPicker). */
interface GrnPickerRow {
  key: string;
  product: any;
  variant: any;
  sku: string;
  description: string;
  ean: string;
  uom: string;
  pricing: any[];
  /** Price rows with a real cost — the choices offered in the cost select. */
  costOptions: any[];
  /** No cost on file: cost (and MRP) are typed, not picked. */
  freeCost: boolean;
  costPrice: number | null;
  mrp: number;
  qty: number;
  allowDecimal: boolean;
  unpriced: boolean;
  inList: boolean;
  selected: boolean;
  /** Failed the last "Add lines" check (qty or cost missing). */
  invalid?: boolean;
}

/** The confirm dialog renders its message as HTML. */
function escapeHtml(text: string): string {
  return String(text).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** What the browser safety copy of an unsaved GRN holds (see autosaveDraft). */
interface GrnDraftSnapshot {
  selectedVendorId: string | null;
  /** The vendor row itself, so the picker can label it before the list loads. */
  vendor: any;
  vendorDisabled: boolean;
  selectedPoNumber: string | null;
  selectedPoData: any;
  selectedGinData: any;
  documentLocationId: string | null;
  invoiceNumber: string;
  invoiceDate: string;
  receivedDate: string;
  invoiceAmount: number;
  freightCharges: number;
  packingCharges: number;
  otherDiscounts: number;
  overallDiscountAmount: number;
  costPriceIncludesGst: boolean;
  editReasonId: string | null;
  editReasonNote: string;
  grnItems: any[];
}

@Component({
  selector: 'app-create-grn',
  standalone: true,
  imports: [
    NgSelectModule,
    SearchSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    RouterLink,
    WarehouseSelectComponent,
    QtyInputDirective,
    StickyColsDirective,
  ],
  templateUrl: './create-grn.component.html',
  styleUrl: './create-grn.component.scss',
})
export class CreateGrnComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Whether this line may be keyed in fractions - bulk, weighable, bulk-child
   * or a kg/gm/ltr unit. Counted SKUs (PCS, NOS, PKT ...) stay whole. The
   * server applies the same rule on save.
   */
  allowsDecimal(source: any): boolean {
    return allowsDecimalQty(source);
  }

  private poService = inject(PoService);
  private router = inject(Router);
  private grnService = inject(GrnService);
  private toastr = inject(ToastrService);
  private route = inject(ActivatedRoute);
  private vendorService = inject(VendorService);
  private productService = inject(ProductsService);
  private manufacturerService = inject(ManufacturerService);
  private confirmDialog = inject(ConfirmDialogService);
  private configurationService = inject(ConfigurationService);
  private prnService = inject(PrnService);
  private permissionService = inject(PermissionService);
  private isSaved = false;
  private authService = inject(AuthService);
  private gstSlabService = inject(GstSlabService);
  private cdr = inject(ChangeDetectorRef);
  private barcodeScannerService = inject(BarcodeScannerService);
  private reasonsService = inject(ReasonsService);
  private zone = inject(NgZone);
  private barcodeSubscription!: Subscription;
  isPricingFormVisible: boolean = false;
  editingItem: any = null; // ✅ Track which item is being edited

  poWithoutGrnOptions: any[] = [];
  /**
   * Today in IST (yyyy-MM-dd). Not toISOString() — that yields the UTC date,
   * which is still yesterday between 00:00 and 05:30 IST, and not the browser's
   * local date either, so a mis-set terminal clock zone can't shift the day.
   */
  todayDate = istToday();
  poOptions: any[] = [];

  /**
   * ng-select only matches bindLabel by default, so typing a vendor name would
   * find nothing. Arrow property so `this` survives being passed as an input.
   */
  searchDocByNumberOrVendor = (term: string, item: any): boolean => {
    const needle = (term || '').toLowerCase();
    return [item?.poNumber, item?.ginNumber, item?.vendorName]
      .filter(Boolean)
      .some((field: string) => String(field).toLowerCase().includes(needle));
  };
  poSearch$ = new Subject<string>();
  grnId: string = '';
  isSearching = false;
  selectedPoNumber: string | null = null;
  selectedPoData: any = null;
  // The store/warehouse this GRN receives into. Display-only: it follows the
  // header switcher for a new GRN, the linked PO's own location once a PO is
  // selected, and the saved GRN's location when editing/viewing.
  selectedWarehouse: WarehouseOption | null = null;
  documentLocationId: string | null = null;

  /**
   * Location to scope product searches to: a Store-type location makes the
   * backend return DSD products only.
   */
  get searchLocationId(): string | undefined {
    return this.selectedWarehouse?.locationId ?? undefined;
  }

  onWarehouseChange(warehouse: WarehouseOption | null) {
    this.selectedWarehouse = warehouse;
    // Keep the previewed GRN number's prefix in sync with the picker.
    if (!this.isEdit) {
      this.grnService
        .getNextGrnNumber(warehouse?.storeId ?? undefined)
        .subscribe({
          next: (res: any) => {
            this.grnNumber = res?.grnNumber || this.grnNumber;
          },
          error: () => {},
        });
    }
  }
  selectedGinData: any = null;
  ginWithoutGrnOptions: { ginNumber: string; ginId: string }[] = [];
  ginOptions: any[] = [];
  grnItems: any[] = [];

  invoiceNumber: string = '';
  invoiceDate: string = '';
  /**
   * Configuration > "GRN backdating limit (days)". null = no limit. Applies to
   * BOTH the invoice date and the received date. Drives the date pickers' `min`;
   * the server enforces the same rule on save.
   */
  backdateLimitDays: number | null = null;
  /** Earliest selectable GRN date (yyyy-MM-dd), or '' when unlimited. */
  minGrnDate: string = '';
  /** Status of the GRN being edited; '' while creating a new one. */
  grnStatus: string = '';
  /**
   * Configuration > "Allow editing posted GRNs". null until the setting has
   * loaded, so the lock below waits rather than guessing.
   */
  private allowEditPosted: boolean | null = null;
  /**
   * Configuration > "Allow entering received date". OFF (default) keeps the
   * Received Date picker visible but disabled; the server stamps the posting
   * day. Until then the field shows today (what will be stamped if submitted
   * now); a POSTED GRN shows its stamped date.
   */
  allowReceivedDateEntry = false;

  // ── Post-posting amendment trail ─────────────────────────────────────────
  /** Amendments made since this GRN posted; 0 = never amended. */
  grnRevisionNo = 0;
  /** Reason Master entries of type GRN, offered when amending. */
  amendmentReasons: TransferOutReason[] = [];
  editReasonId: string | null = null;
  editReasonNote = '';
  revisionHistoryVisible = false;

  // ── Page UI state (design: collapsible header, variance filters) ──────────
  headerOpen = true;
  lineFilter: 'all' | 'short' | 'excess' | 'pending' = 'all';
  lineQuery = '';
  revisionsLoading = false;
  revisionsError = false;
  revisions: GrnRevision[] = [];
  // Flips true on first Submit click so inline "required" errors light up.
  // Reset on successful save. Doesn't gate the button (we never disable submit).
  submitAttempted: boolean = false;

  // True from the moment a save request leaves until it settles. Prod
  // incident: GRN-864..867 were ONE invoice saved four times — on a flaky
  // link the user pressed Submit again each time nothing came back, and
  // every press opened a fresh POST /grn. While this is set the handler
  // returns immediately and both save buttons are disabled.
  isSubmitting = false;
  summary = { subTotal: 0, gst: 0, total: 0 };
  grnSummary = {
    subTotal: 0,
    lineDiscountAmount: 0,
    taxableValue: 0,
    gstAmount: 0,
    cessAmount: 0,
    schemeDiscountAmount: 0,
    fixedDiscountAmount: 0,
    otherDiscounts: 0,
    freightCharges: 0,
    packingCharges: 0,
    otherCharges: 0,
    grandTotal: 0,
    overallDiscountAmount: 0,
    roundOff: 0,
    finalTotal: 0,
  };
  products: any[] = [];
  productSearch$ = new Subject<string>();
  selectedProduct: any = null;
  selectedVariant: any = null;
  selectedQuantity: number = 1;
  enteredCostPrice: number = 0;
  allCostPrices: number[] = [];
  allMRPs: number[] = [];
  selectedMRP: number = 0;
  grnNumber: string = '';
  freightCharges: number = 0;
  receivedDate: string = this.todayDate;

  // Cost-basis convention for this GRN. Mirrors the PO toggle:
  //   false = basic + GST extra (B2B default)
  //   true  = costPrice is GST-inclusive (CGST Rule 35 back-calc)
  // Inherited from the linked PO when the GRN is created against one;
  // otherwise from the vendor's saved default.
  costPriceIncludesGst: boolean = false;
  enableProductSearch: Boolean = false;
  packingCharges: number = 0;
  otherDiscounts: number = 0;
  overallDiscountAmount: number = 0;
  invoiceAmount: number = 0; // User-entered invoice amount for validation
  vendorDisabled: boolean = false;
  isEdit: boolean = false;
  totalPrice: number = 0;

  // Product-related fields
  vendors: any[] = [];
  vendorSearch$ = new Subject<string>(); // Added for search
  isVendorLoading = false; // Loading indicator
  private _selectedVendorId: any | null = null;
  /**
   * Vendor for this GRN. Setter-backed on purpose: the vendor is assigned from
   * five different places (manual pick, PO prefill, GIN prefill, edit load,
   * reset), so hanging the open-PRN lookup off a single change handler would
   * silently miss most of them.
   */
  get selectedVendorId(): any | null {
    return this._selectedVendorId;
  }
  set selectedVendorId(value: any | null) {
    const next = value ?? null;
    if (next === this._selectedVendorId) return;
    this._selectedVendorId = next;
    this.loadOpenPrns(next);
  }

  // ── Vendor returns (PRNs) ────────────────────────────────────────────────
  // awaitingHandover: POSTED — stock debited, goods staged in the returns
  // area. The actionable set: load onto this vendor's vehicle and mark
  // dispatched. inPreparation: paperwork-stage PRNs, informational only.
  awaitingPrns: OpenPrn[] = [];
  awaitingPrnCount = 0;
  awaitingPrnValue = 0;
  prepPrnCount = 0;
  prepPrnValue = 0;
  openPrnsLoading = false;
  openPrnsError = false;
  openPrnsExpanded = false;
  /** PRN ids with an in-flight dispatch call (disables their button). */
  dispatching = new Set<string>();

  viewOnly = false;

  productCodeSearch$ = new Subject<string>();
  searchResults: any[] = [];
  isCodeSearching = false;
  /** "Search by EAN" box — resolves identity barcodes and price-generation EANs. */
  productEanSearch$ = new Subject<string>();
  eanSearchResults: any[] = [];
  isEanSearching = false;
  selectedSearchType: 'sku' | 'plu' | 'ean' | 'query' = 'sku';
  searchTypes = [
    { value: 'sku', label: 'SKU' },
    { value: 'plu', label: 'PLU' },
    { value: 'ean', label: 'EAN' },
    { value: 'query', label: 'General' },
  ];
  newPricing: {
    basePrice: number;
    costPrice: number;
    sellingPrice: number;
    mrp: number;
    eanCode: string;
  } = {
      basePrice: 0,
      costPrice: 0,
      sellingPrice: 0,
      mrp: 0,
      eanCode: '',
    };
  // "Selling price same as MRP?" is asked once per pricing popup; a yes
  // makes SP follow MRP until SP is typed.
  sellingAtMrpAsked = false;
  sellingFollowsMrp = false;
  currentEanCode: string = '';
  /** All barcodes of the selected variant, shown in the Add Pricing modal. */
  variantBarcodes: string[] = [];
  variantPrimaryEan = '';
  newPricingValidationError: string | null = null;
  // CP-from-MRP is opt-in per popup: OFF by default so keying an MRP never
  // silently rewrites a manually entered cost price. When enabled, the
  // margin % (seeded from the product master) drives CP = MRP * (1 - m/100).
  useMarginForCp = false;
  pricingMarginPercent = 0;
  private lastValidInvoiceDate: string = this.invoiceDate;
  private lastValidReceivedDate: string = this.receivedDate;

  constructor() { }

  ngOnInit(): void {
    this.viewOnly = this.route.snapshot.queryParamMap.get('view') === 'true';
    this.loadBackdateLimit();
    this.loadAllowEditPosted();
    this.loadAllowReceivedDateEntry();
    this.loadAmendmentReasons();
    this.route.params.subscribe((params) => {
      const grnId = params['id'];

      if (grnId) {
        this.grnId = grnId;
        this.isEdit = true;
        this.initDraft(grnId); // restore is offered once the GRN has loaded
        this.loadGrnData(grnId); // load existing GRN
      } else {
        this.initDraft(null);
        // After the first render: the restore question is a modal.
        setTimeout(() => this.offerDraftRestore());
        this.grnService.getNextGrnNumber().subscribe({
          next: (res: any) => {
            this.grnNumber = res?.grnNumber || '';
          },
          error: (err) => {
            this.toastr.error('Failed to fetch next GRN number', 'Error');
            console.error('Error fetching next GRN number', err);
          },
        });
      }
    });

    this.vendorService.getVendors().subscribe((res: any) => {
      this.vendors = res?.data || [];
    });

    this.productSearch$
      .pipe(
        debounceTime(300),
        tap(() => (this.isSearching = true)),
        switchMap((term) => {
          if (term?.length >= 3) {
            // Scope name/text search to the selected vendor when one is chosen,
            // so users can only add items the vendor actually supplies.
            return this.poService
              .searchProducts(
                term,
                this.selectedVendorId || undefined,
                this.searchLocationId,
              )
              .pipe(
                tap(() => (this.isSearching = false)),
                catchError(() => {
                  this.isSearching = false;
                  return of([]);
                }),
              );
          } else {
            this.isSearching = false;
            return of([]);
          }
        }),
      )
      .subscribe((results: any) => {
        this.products = results?.data || results || [];
      });

    this.productCodeSearch$
      .pipe(
        // A new term makes the previous scan's rows stale: drop them so
        // neither Enter nor a click can pick the earlier product. No
        // distinctUntilChanged — scanning the same code again must search.
        tap((term) => {
          this.skuScan.termTyped(term);
          this.searchResults = [];
        }),
        debounceTime(300),
        filter((term) => !!term && term.length >= 2), // Minimum 2 characters for code search
        tap(() => (this.isCodeSearching = true)),
        switchMap((term) =>
          // Vendor-scoped SKU search — matches the name-search behavior above.
          this.productService
            .searchBySKUWithContext(
              term,
              'GRN',
              this.selectedVendorId || undefined,
            )
            .pipe(
              map((data: any) => ({ term, data: data || [] })),
              catchError((error) => {
                console.error('Error searching by SKU:', error);
                return of({ term, data: [] as any[] });
              }),
              tap(() => (this.isCodeSearching = false)),
            ),
        ),
      )
      .subscribe(({ term, data }) => {
        // ✅ Format search results for display
        this.searchResults = data.map((item: any) => ({
          ...item,
          ean: currentEanOf(item.variants?.[0]),
          displayText: `${item.sku} - ${item.longDescription} `,
        }));
        this.isCodeSearching = false;
        this.skuScan.resultsArrived(term, this.searchResults);
      });

    this.productEanSearch$
      .pipe(
        // Same stale-row guard as the SKU box above.
        tap((term) => {
          this.eanScan.termTyped(term);
          this.eanSearchResults = [];
        }),
        debounceTime(300),
        filter((term) => !!term && term.trim().length >= 3),
        tap(() => (this.isEanSearching = true)),
        switchMap((term) =>
          // Vendor-scoped like the SKU / name searches above; a Store-type
          // receiving location restricts to DSD products server-side.
          this.productService
            .searchProducts({
              ean: term.trim(),
              context: 'GRN',
              vendorId: this.selectedVendorId || undefined,
              locationId: this.searchLocationId,
            })
            .pipe(
              map((res: any) => ({ term, data: withMatchedEan(res?.data || [], term) })),
              catchError((error) => {
                console.error('Error searching by EAN:', error);
                return of({ term, data: [] as any[] });
              }),
              tap(() => (this.isEanSearching = false)),
            ),
        ),
      )
      .subscribe(({ term, data }) => {
        this.eanSearchResults = data;
        this.isEanSearching = false;
        this.eanScan.resultsArrived(term, data);
      });

    this.poService.getPurchaseOrdersWithoutGrn().subscribe({
      next: (res: any) => {
        this.poWithoutGrnOptions = res;
        this.poOptions = res.map((po: any) => ({
          poNumber: po.poNumber || po.purchaseOrder.poNumber,
          id: po.poId,
          vendorName: po.vendorName ?? null,
        }));
      },
      error: (err) => {
        console.error('Error loading POs without GRN', err);
      },
    });

    this.grnService.getGinsWithoutGrn().subscribe({
      next: (res: any) => {
        this.ginWithoutGrnOptions = res;
        this.ginOptions = res.map((gin: any) => ({
          ginNumber: gin.ginNumber,
          id: gin.ginId,
          vendorName: gin.vendorName ?? null,
        }));
      },
      error: (err) => {
        console.error('Error loading GINs without GRN', err);
      },
    });

    this.poSearch$
      .pipe(
        debounceTime(300),
        tap(() => (this.isSearching = true)),
        switchMap((poNumber) => {
          if (poNumber?.length >= 3) {
            return this.poService.searchPo(poNumber).pipe(
              tap(() => (this.isSearching = false)),
              catchError(() => {
                this.isSearching = false;
                return of([]);
              }),
            );
          } else {
            this.isSearching = false;
            return of([]);
          }
        }),
      )
      .subscribe((results: any) => {
        this.poOptions = results;
      });

    this.vendorSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: string) => {
        if (searchTerm && searchTerm.trim().length > 0) {
          this.isVendorLoading = true;

          this.vendorService
            .searchVendors({ name: searchTerm.trim() })
            .subscribe({
              next: (response) => {
                this.vendors = (response as any)?.data || [];
                this.isVendorLoading = false;
              },
              error: (err) => {
                console.error('Error fetching vendors:', err);
                this.vendors = [];
                this.isVendorLoading = false;
              },
            });
        } else {
          this.vendors = [];
        }
      });
    this.vendorService.getAllVendorsList().subscribe((res: any) => {
      this.vendors = res || [];
    });

    this.barcodeSubscription = this.barcodeScannerService.barcodeScanned$.subscribe(
      (barcode) => {
        this.handleBarcodeScan(barcode);
      },
    );
  }

  @ViewChild('poSelect') poSelect!: NgSelectComponent;
  @ViewChild('skuScanSelect') skuScanSelect?: NgSelectComponent;
  @ViewChild('eanScanSelect') eanScanSelect?: NgSelectComponent;
  /** New-pricing dialog: land the cursor in Base price with its value selected. */
  @ViewChild('basePriceInput') set basePriceInputRef(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref) setTimeout(() => { ref.nativeElement.focus(); ref.nativeElement.select(); });
  }
  /** Holds a scanner's early Enter until its own search answers (see ScanTypeahead). */
  skuScan = new ScanTypeahead(() => this.skuScanSelect);
  eanScan = new ScanTypeahead(() => this.eanScanSelect);
  @ViewChild('quantityInput') quantityInputRef!: ElementRef;
  @ViewChildren('receivedQtyInput')
  receivedQtyInputs!: QueryList<ElementRef>;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.poSelect?.focus();
    });
  }

  ngOnDestroy(): void {
    if (this.barcodeSubscription) {
      this.barcodeSubscription.unsubscribe();
    }
    this.pickerSearchSub?.unsubscribe();
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  handleBarcodeScan(barcode: string) {
    // Scan hygiene (core/utils/scan-code.util): a pack's marketing QR (URL)
    // is not an item; a GS1 Digital Link / element string matches by GTIN.
    const scan = resolveScanCode(barcode);
    if (scan.kind === 'ignored') {
      if (scan.reason !== 'empty') {
        this.toastr.warning(ignoredScanMessage(scan), 'Scan ignored');
      }
      return;
    }
    barcode = scan.code;
    // The scanner's burst is swallowed after its first keys, which can leave
    // those keys (and the previous scan's rows) in a focused scan box.
    this.skuScanSelect?.close();
    this.eanScanSelect?.close();

    // A scan may be an EAN/barcode OR a product SKU — match rows on either.
    const matchedItems = this.grnItems.filter(
      (item) => item.eanCode === barcode || item.skuCode === barcode,
    );

    if (matchedItems.length > 1) {
      this.toastr.warning(
        `Multiple items found with barcode: ${barcode}. Please select manually.`,
        'Multiple Matches',
      );
      return;
    }

    if (matchedItems.length === 1) {
      // ✅ HYBRID: Increment + Focus/Select
      const matchedItem = matchedItems[0];
      matchedItem.receivedQuantity =
        (Number(matchedItem.receivedQuantity) || 0) + 1;
      this.updateGrnSummary();

      const index = this.grnItems.indexOf(matchedItem);
      this.focusTableInput(index, `Incremented ${matchedItem.skuCode}`);
      return;
    }

    // ✅ AUTO-ADD: If not in table, search in master list
    this.toastr.info(
      `Searching master list for: ${barcode}...`,
      'Barcode Search',
    );
    this.poService.searchProducts(barcode, this.selectedVendorId || undefined, this.searchLocationId).subscribe(
      (results: any) => {
        const productList = (results as any)?.data || results || [];
        if (productList && productList.length > 0) {
          // Prefer an exact barcode match (identity barcode, or an old
          // pack's EAN on a prior pricing row); else a product whose SKU is
          // the scanned code (SKU scan); else the first result.
          const product =
            productList.find((p: any) =>
              p.variants?.some((v: any) => variantHasCode(v, barcode)),
            ) ||
            productList.find((p: any) => p.sku === barcode) ||
            productList[0];
          this.autoAddItemToTable(product, barcode);
        } else {
          this.toastr.error(
            `No product found with barcode: ${barcode}`,
            'Not Found',
          );
        }
      },
      (err) => {
        console.error('Error searching product master:', err);
        this.toastr.error('Search failed', 'Error');
      },
    );
  }

  private focusTableInput(index: number, toastMessage: string = '') {
    // We need a small timeout for ViewChildren to potentially update if a row was just added
    setTimeout(() => {
      const inputArr = this.receivedQtyInputs.toArray();
      const targetedInput = inputArr[index];

      if (targetedInput) {
        targetedInput.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        const el = targetedInput.nativeElement;
        el.classList.add('scanned-highlight');
        setTimeout(() => el.classList.remove('scanned-highlight'), 2000);

        el.focus();
        el.select();

        if (toastMessage) {
          this.toastr.success(toastMessage, 'Barcode Scanned');
        }
      }
    }, 100);
  }

  private autoAddItemToTable(product: any, barcode: string) {
    const variant =
      product.variants?.find((v: any) => variantHasCode(v, barcode)) ||
      product.variants?.[0];
    if (!variant) return;

    // No usable price yet (barcode tagged, pricing deferred to this GRN):
    // don't auto-add a ₹0 line. Open the "Add products" dialog with the item
    // pre-selected so the operator keys the cost / MRP right there.
    if (!hasPurchasePrice(variant)) {
      this.warnIfUnpriced(product, variant);
      this.openProductPicker(product);
      return;
    }

    const pricing = variant.pricing?.[0] || {};
    const costPrice = +pricing.costPrice || 0;
    const gstRate = parseFloat(product.purchaseGstRate?.value || '0');
    const cessRate = +product.cessRate || 0;
    // Stored basic cost (excl GST) when it agrees with this cost price,
    // else derived from it (see consistentBasicCost).
    const basicCostPrice = consistentBasicCost(costPrice, gstRate + cessRate, pricing.basePrice);
    const quantity = product.innerPackaging || 1;
    const breakup = this.computeLineTaxBreakup(
      basicCostPrice, costPrice, quantity, gstRate, cessRate,
    );
    const actualCost = breakup.actualCostPrice;
    const gstAmount = breakup.gstAmount;
    const totalWithGST = breakup.lineGross;

    const newItem = {
      productVariant: variant,
      productVariantId: variant.id,
      skuCode: product.sku || '',
      pluCode: '',
      description: product.longDescription || '',
      eanCode: currentEanOf(variant),
      weightKg: +product.weightKg || +variant.weight || 0,
      innerQuantity: product.innerPackaging || 1,
      uom: product.uom || '',
      // Decide whether this line's quantity box accepts decimals: a kg/gm/ltr
      // unit, or a bulk / weighable / bulk-child product.
      isBulk: product?.isBulk ?? null,
      isWeighable: product?.isWeighable ?? null,
      bulkParentProductId: product?.bulkParentProductId ?? null,
      poQuantity: quantity,
      receivedQuantity: quantity,
      freeQuantity: 0,
      actualCostPrice: actualCost,
      gstPercent: gstRate,
      gstAmount,
      costPrice,
      mrp: Number(pricing.mrp || 0),
      sellingPrice: Number(pricing.sellingPrice || 0),
      marginPercentage:
        product.marginPercentage || product.margin_percentage || 0,
      freightCharges: 0,
      packingCharges: 0,
      totalPrice: totalWithGST,
      basicCostPrice,
      discountOnePercent: 0,
      discountOneAmount: 0,
      discountTwoPercent: 0,
      discountTwoAmount: 0,
      cashDiscountPercent: 0,
      cashDiscountAmount: 0,
      schemeDiscountPercent: 0,
      schemeDiscountAmount: 0,
      fixedDiscountPercent: 0,
      fixedDiscountAmount: 0,
      overAllDiscountAmount: 0,
      oldRoiPercent: 0,
      newRoiPercent: 0,
      hsnCode: product.hsnCode || '',
      cessRate: cessRate,
      cessAmount: breakup.cessAmount,
      basicCostTotal: breakup.basicCostTotal,
      totalPriceWithoutGST: breakup.basicCostTotal,
    };

    // Sanitize and Add
    this.sanitizeItemPricing(newItem);
    this.grnItems.push(newItem);
    this.updateGrnSummary();

    // Focus newly added item
    this.focusTableInput(this.grnItems.length - 1, `Added ${newItem.skuCode}`);
  }
  /**
   * @deprecated DEAD CODE — nothing calls this. The live path is
   * onPoIdChange() → poService.getPendingItems() → onPoSelected(), and
   * onPoSelected() is the mapper that actually builds grnItems. Edit that
   * one; changes made here have no effect. Kept only to avoid touching
   * unrelated behavior — safe to delete.
   */
  loadPoPendingItems(poId: string): void {
    if (!poId) return;

    this.poService.getPendingItems(poId).subscribe({
      next: (response: any) => {
        // Safely extract the pending items array
        const pendingItems: any[] =
          response?.data || response?.items || response || [];

        this.toastr.info(`Found ${pendingItems.length} pending items for PO.`);
        console.log('[GRN Raw PO Items]:', pendingItems);

        // Map the API structure to the local grnItems structure
        // Inherit PO's cost-basis convention so amounts reconcile with the PO.
        const poCostFlag = (response as any)?.costPriceIncludesGst
          ?? (response as any)?.purchaseOrder?.costPriceIncludesGst
          ?? pendingItems?.[0]?.purchaseOrder?.costPriceIncludesGst;
        if (typeof poCostFlag === 'boolean') this.costPriceIncludesGst = poCostFlag;

        this.grnItems = pendingItems.map((item: any) => {
          const variant = item.productVariant;
          const product = variant?.product || {};
          const costPrice = +item.costPrice || 0;
          // GST/cess as of TODAY from the product master (backend enriches
          // currentGstPercent/currentCessRate). If the slab changed between
          // PO and receipt, the vendor invoice carries the new rate — the
          // GRN must follow the product master, not the PO snapshot.
          const gstPercent =
            item.currentGstPercent != null
              ? +item.currentGstPercent
              : +item.gstPercent || 0;
          const cessRate =
            item.currentCessRate != null
              ? +item.currentCessRate
              : +item.cessRate || 0;

          if (gstPercent !== (+item.gstPercent || 0)) {
            this.toastr.info(
              `GST for ${product?.sku || ''} updated from ${+item.gstPercent || 0}% (PO) to ${gstPercent}% (current product master)`,
              'GST Rate Changed'
            );
          }

          // Basic paired with THIS cost price: the matching price row, then
          // the PO's stored basic — each only if it agrees with the cost —
          // else derived. pricing[0] may be a different row (wrong basic).
          const basicCostPrice = consistentBasicCost(
            costPrice,
            +item.gstPercent + (+item.cessRate || 0) || gstPercent + cessRate,
            pricingForCost(variant?.pricing, costPrice)?.basePrice,
            item.actualCostPrice,
          );

          const receivedQuantity = item.pendingQuantity || item.quantity || 0;
          const breakup = this.computeLineTaxBreakup(
            basicCostPrice, costPrice, receivedQuantity, gstPercent, cessRate,
          );
          const gstAmount = breakup.gstAmount;
          const totalPriceWithGST = breakup.lineGross;
          const totalPriceWithoutGST = breakup.basicCostTotal;

          return {
            productVariant: variant,
            productVariantId: item.productVariantId,
            skuCode: product?.sku || item.skuCode || '',
            pluCode: '',
            description: product?.longDescription || '',
            eanCode: currentEanOf(variant),
            innerQuantity: product?.innerPackaging || 1,
            uom: product?.uom || '',
            poQuantity: item.quantity,
            // Decide whether this line's quantity box accepts decimals: a kg/gm/ltr
            // unit, or a bulk / weighable / bulk-child product.
            isBulk: product?.isBulk ?? null,
            isWeighable: product?.isWeighable ?? null,
            bulkParentProductId: product?.bulkParentProductId ?? null,
            receivedQuantity: receivedQuantity, // Use pending/ordered quantity as default
            freeQuantity: item.freeQuantity || 0,
            actualCostPrice: basicCostPrice, // Basic Cost Price (w/o GST)
            basicCostPrice,
            gstPercent,
            gstAmount: gstAmount,
            costPrice: +item.costPrice, // Cost Price (with GST)
            cessRate: cessRate,
            cessAmount: breakup.cessAmount,
            mrp: Number(
              item.mrp ||
                (pricingForLine(variant?.pricing, item.costPrice, item.mrp) ?? variant?.pricing?.[0])?.mrp ||
                0,
            ),
            sellingPrice: Number(
              item.sellingPrice ||
                (pricingForLine(variant?.pricing, item.costPrice, item.mrp) ?? variant?.pricing?.[0])
                  ?.sellingPrice ||
                0,
            ),
            freightCharges: 0,
            packingCharges: 0,
            totalPrice: totalPriceWithGST, // Total Price (with GST)
            discountOnePercent: 0,
            discountOneAmount: 0,
            discountTwoPercent: 0,
            discountTwoAmount: 0,
            cashDiscountPercent: 0,
            cashDiscountAmount: 0,
            schemeDiscountPercent: 0,
            schemeDiscountAmount: 0,
            fixedDiscountPercent: 0,
            fixedDiscountAmount: 0,
            overAllDiscountAmount: 0,
            totalPriceWithoutGST: totalPriceWithoutGST, // Total Price (w/o GST)
            oldRoiPercent: 0,
            newRoiPercent: +(
              ((item.mrp - item.costPrice) / item.mrp) *
              100
            ).toFixed(2),
            hsnCode: product?.hsnCode || '',
            id: item?.id,
            expiryDate: null,
            marginPercentage: Number(item.margin_percentage || item.marginPercentage || product?.marginPercentage || product?.margin_percentage || 0),
          };
        });

        // ✅ SANITIZE: Ensure all loaded items have valid pricing relations
        this.grnItems.forEach(item => this.sanitizeItemPricing(item));

        // Update the overall summary after loading items
        this.updateGrnSummary();
      },
      error: (err) => {
        this.toastr.error('Failed to load PO pending items', 'Error');
        console.error('Error fetching pending items:', err);
      },
    });
  }
  loadGrnData(grnId: string) {
    this.grnService.getGrnById(grnId).subscribe({
      next: (data: any) => {
        // Restore the cost-basis convention this GRN was created with so
        // subsequent edits recompute under the same semantics.
        this.costPriceIncludesGst = !!data?.costPriceIncludesGst;

        this.grnStatus = data?.status || '';
        this.grnRevisionNo = Number(data?.revisionNo || 0);
        this.applyPostedEditLock();

        // A saved GRN keeps ITS OWN store/warehouse, whatever the viewer is
        // currently working as.
        this.documentLocationId = data?.store?.locationId ?? null;

        this.selectedPoData = {
          id: data.purchaseOrderId,
          poNumber: data.poNumber || data?.purchaseOrder?.poNumber || '',
          vendor: data.vendor || data.vendorId,
        };
        const isVendorPresent = this.vendors.some(
          (v: any) => v.id === this.selectedVendorId,
        );
        if (!isVendorPresent && data.vendor) {
          this.vendors = [...this.vendors, data.vendor];
        }

        this.selectedPoNumber =
          data.poNumber || data?.purchaseOrder?.poNumber || '';

        const isPoPresent = this.poOptions.some(
          (po: any) => po.id === this.selectedPoData.id,
        );
        if (!isPoPresent && this.selectedPoData.id) {
          this.poOptions = [
            ...this.poOptions,
            {
              poNumber: this.selectedPoData.poNumber,
              id: this.selectedPoData.id,
            },
          ];
        }
        this.selectedVendorId = data.vendor?.id || data.vendorId;

        this.grnNumber = data.grnNumber;
        this.receivedDate = data.receivedDate?.split('T')[0] || this.todayDate;
        this.syncReceivedDateForConfig();
        this.invoiceNumber = data.invoiceNumber;
        this.invoiceAmount = data.invoiceAmount || 0;
        this.invoiceDate = data.invoiceDate?.split('T')[0] || '';
        // Keep the revert targets in step with the loaded GRN, otherwise
        // rejecting an edited date would snap the field back to the blank
        // initial value instead of the date this GRN was saved with.
        this.lastValidInvoiceDate = this.invoiceDate;
        this.lastValidReceivedDate = this.receivedDate;
        this.packingCharges = data.packingCharges || 0;
        this.freightCharges = data.freightCharges || 0;
        this.otherDiscounts = data.otherDiscountAmount || 0;
        this.overallDiscountAmount = data.overallDiscountAmount || 0;

        // Handle GIN Data on Edit
        if (data.ginId) {
          const ginId = data.ginId;
          const ginNumber = data.ginNumber || data.gin?.ginNumber;

          if (ginNumber) {
            // If we already have the number, just set it
            this.setGinOption(ginId, ginNumber);
          } else {
            // Fetch the GIN details to get the number
            this.grnService.getGinById(ginId).subscribe({
              next: (gin: any) => {
                this.setGinOption(ginId, gin.ginNumber);
              },
              error: (err) => console.error('Error fetching linked GIN', err),
            });
          }
        }

        this.grnItems = data?.items?.map((item: any) => ({
          productVariant: item.productVariant, // ✅ Needed for New Pricing modal
          productVariantId: item.productVariantId,
          skuCode: item.skuCode,
          uom: item.uom,
          description: item.productVariant.product.longDescription,
          eanCode: item.eanCode,
          innerQuantity: item.innerQuantity,
          poQuantity: item.poQuantity,
          receivedQuantity: item.receivedQuantity,
          freeQuantity: item.freeQuantity,
          costPrice: item.costPrice,
          actualCostPrice: item.actualCostPrice,
          gstPercent: item.gstPercent,
          gstAmount: item.gstAmount,
          cessRate: item.cessRate,
          cessAmount: item.cessAmount,
          mrp: Number(item.mrp || 0),
          sellingPrice: Number(item.sellingPrice || 0),
          freightCharges: item.freightCharges,
          marginPercentage: Number(item.marginPercentage || item.margin_percentage || item.productVariant?.product?.marginPercentage || item.productVariant?.product?.margin_percentage || 0),
          discountOnePercent: item.discountOnePercent,
          discountOneAmount: item.discountOneAmount,
          discountTwoPercent: item.discountTwoPercent,
          discountTwoAmount: item.discountTwoAmount,
          cashDiscountPercent: item.cashDiscountPercent,
          cashDiscountAmount: item.cashDiscountAmount,
          schemeDiscountPercent: item.schemeDiscountPercent,
          schemeDiscountAmount: item.schemeDiscountAmount,
          fixedDiscountPercent: item.fixedDiscountPercent,
          fixedDiscountAmount: item.fixedDiscountAmount,
          overAllDiscountAmount: item.overAllDiscountAmount,
          oldRoiPercent: item.oldRoiPercent,
          newRoiPercent: item.newRoiPercent,
          hsnCode: item.hsnCode,
          id: item.id,
          packingCharges: item.packingCharges || 0,
          batchNumber: item.batchNumber || '',
          expiryDate: item.expiryDate ? item.expiryDate.split('T')[0] : null,
          totalPrice: item.totalPrice || 0,
          totalPriceWithoutGST:
            item.totalPriceWithoutGST ||
            +(
              (item.actualCostPrice || 0) * (item.receivedQuantity || 0)
            ).toFixed(3),
        }));

        // ✅ SANITIZE
        this.grnItems.forEach(item => this.sanitizeItemPricing(item));

        this.refreshDraftLinesFromMaster(data);
        this.updateGrnSummary();
        this.offerDraftRestore(data?.updatedAt);
      },
      error: (err) => {
        this.toastr.error('Failed to load GRN data', 'Error');
        console.error('Error loading GRN:', err);
      },
    });
  }

  /**
   * DRAFT only: pull today's product-master values (GST %, cess, HSN, UOM,
   * inner pack, margin) into every line and recompute its tax, so master
   * corrections made after the draft was saved show up when it is reopened.
   *
   * The backend attaches the `current*` fields to DRAFT documents only, so a
   * POSTED GRN keeps its snapshot untouched. Runs in view mode as well — a
   * draft opened read-only must still show today's rates, and nothing is
   * written until the user edits and saves. The raw API rows are read here (the
   * grnItems mapping above deliberately copies only the columns it needs),
   * so nothing extra reaches the save payload. Prices the user typed (cost
   * price, MRP, selling price, discounts) and the scanned EAN are never
   * overwritten.
   */
  private refreshDraftLinesFromMaster(data: any): void {
    if (data?.status !== 'DRAFT') return;

    const rawById = new Map<string, any>(
      (data?.items || []).map((i: any) => [i.id, i]),
    );
    const changed: string[] = [];

    for (const item of this.grnItems) {
      const raw = rawById.get(item.id);
      if (!raw) continue;
      const hasSnapshot =
        raw.currentGstPercent != null || raw.currentHsnCode != null;
      if (!hasSnapshot) continue;

      const diffs: string[] = [];
      let taxChanged = false;

      const oldGst = +(item.gstPercent || 0);
      const oldCess = +(item.cessRate || 0);
      const newGst =
        raw.currentGstPercent != null ? +raw.currentGstPercent : oldGst;
      const newCess = +(raw.currentCessRate ?? 0);
      if (newGst !== oldGst) {
        diffs.push(`GST ${oldGst}% → ${newGst}%`);
        item.gstPercent = newGst;
        taxChanged = true;
      }
      if (newCess !== oldCess) {
        diffs.push(`Cess ${oldCess}% → ${newCess}%`);
        item.cessRate = newCess;
        taxChanged = true;
      }

      const textFields: Array<[string, string, string]> = [
        ['currentHsnCode', 'hsnCode', 'HSN'],
        ['currentUom', 'uom', 'UOM'],
      ];
      for (const [src, dst, label] of textFields) {
        const v = raw[src];
        if (v != null && v !== '' && String(v) !== String(item[dst] ?? '')) {
          diffs.push(`${label} ${item[dst] || '-'} → ${v}`);
          item[dst] = v;
        }
      }
      if (
        raw.currentInnerQuantity != null &&
        +raw.currentInnerQuantity !== +(item.innerQuantity || 0)
      ) {
        diffs.push(`Inner pack ${+(item.innerQuantity || 0)} → ${+raw.currentInnerQuantity}`);
        item.innerQuantity = +raw.currentInnerQuantity;
      }
      if (
        raw.currentMarginPercent != null &&
        +raw.currentMarginPercent !== +(item.marginPercentage || 0)
      ) {
        diffs.push(`Margin ${+(item.marginPercentage || 0)}% → ${+raw.currentMarginPercent}%`);
        item.marginPercentage = +raw.currentMarginPercent;
      }

      if (taxChanged) this.onPriceChange(item, ''); // recompute line tax + totals
      if (diffs.length) changed.push(`${item.skuCode || ''} (${diffs.join(', ')})`);
    }

    if (changed.length) {
      const shown = changed.slice(0, 5).join('; ');
      const more = changed.length > 5 ? ` and ${changed.length - 5} more` : '';
      this.toastr.info(
        `${changed.length} line(s) updated from the SKU master: ${shown}${more}. Save the draft to keep these values.`,
        'Draft refreshed',
        { timeOut: 10000 },
      );
    }
  }

  onPoSelected(po: any) {
    if (po) {
      this.selectedPoNumber = po.poNumber;
      this.selectedPoData = po;
      this.vendorDisabled = true;
      // Receive into the PO's own store/warehouse, not wherever the receiver
      // happens to be working.
      this.documentLocationId =
        po.store?.locationId ?? po.purchaseOrder?.store?.locationId ?? null;

      // Inherit the PO's cost-basis convention so GRN totals reconcile with
      // the PO (and with the vendor's invoice that follows the same rules).
      if (typeof po.costPriceIncludesGst === 'boolean') {
        this.costPriceIncludesGst = po.costPriceIncludesGst;
      }

      // ✅ Fix: Ensure vendor is selected when PO is selected
      const vendorObj = po.vendor || po.purchaseOrder?.vendor;
      const vendorId =
        po.vendorId ||
        vendorObj?.id ||
        po.purchaseOrder?.vendorId ||
        po.purchaseOrder?.vendor?.id;

      if (vendorObj && typeof vendorObj === 'object') {
        if (!this.vendors.some((v) => v.id === vendorObj.id)) {
          this.vendors = [...this.vendors, vendorObj];
        }
      }

      if (vendorId) {
        this.selectedVendorId = String(vendorId);
        this.cdr.detectChanges();
      }

      // Collected so one summary toast is shown instead of one per line.
      const gstChanges: string[] = [];

      this.grnItems = po?.items.map((item: any) => {
        const variant = item.productVariant;
        const product = variant?.product || {};
        const costPrice = +item.costPrice || 0;

        // GST/CESS as of TODAY from the product master. The pending-items
        // endpoint enriches each line with currentGstPercent/currentCessRate
        // read live from Products.purchaseGstRate. If the slab was updated
        // between PO and receipt, the vendor invoices at the NEW rate, so the
        // GRN must follow the product master, not the PO's snapshot.
        const poGstPercent = +item.gstPercent || 0;
        const poCessRate = +item.cessRate || 0;
        const gstPercent =
          item.currentGstPercent != null
            ? +item.currentGstPercent
            : poGstPercent;
        const cessRate =
          item.currentCessRate != null ? +item.currentCessRate : poCessRate;

        if (gstPercent !== poGstPercent) {
          gstChanges.push(
            `${product?.sku || currentEanOf(variant) || 'item'}: ${poGstPercent}% → ${gstPercent}%`,
          );
        }

        // Basic paired with THIS cost price (matching price row, then the
        // PO's stored basic, each only if consistent), else back-calc at the
        // PO's ORIGINAL rate: costPrice was derived under that rate.
        const basicCostPrice = consistentBasicCost(
          costPrice,
          poGstPercent + poCessRate,
          pricingForCost(variant?.pricing, costPrice)?.basePrice,
          item.actualCostPrice,
        );
        const qty = (item.quantity || 0) + (item.indentQuantity || 0);
        const breakup = this.computeLineTaxBreakup(
          basicCostPrice, costPrice, qty, gstPercent, cessRate,
        );
        const lineRow =
          pricingForLine(variant?.pricing, costPrice, item.mrp) ?? variant?.pricing?.[0];
        return {
          productVariant: item.productVariant,
          productVariantId: item.productVariantId,
          skuCode: product?.sku || '',
          pluCode: '', // to confirm
          description: product?.longDescription || '',
          eanCode: currentEanOf(variant),
          innerQuantity: product?.innerPackaging || 1,
          uom: product?.uom || '',
          // Number(): PO quantities are decimal columns, so a stale build that
          // hands these back as strings must not concatenate them ("10"+"5").
          poQuantity: Number(item.quantity || 0) + Number(item.indentQuantity || 0),
          // Decide whether this line's quantity box accepts decimals: a kg/gm/ltr
          // unit, or a bulk / weighable / bulk-child product.
          isBulk: product?.isBulk ?? null,
          isWeighable: product?.isWeighable ?? null,
          bulkParentProductId: product?.bulkParentProductId ?? null,
          receivedQuantity: Number(item.quantity || 0) + Number(item.indentQuantity || 0),
          freeQuantity: item.freeQuantity || 0,
          actualCostPrice: basicCostPrice,
          basicCostPrice,
          gstPercent,
          gstAmount: breakup.gstAmount,
          costPrice: +item.costPrice,
          cessRate: cessRate,
          cessAmount: breakup.cessAmount,
          // PO lines carry no selling price: use the PO line's own price row.
          mrp: Number(item.mrp || lineRow?.mrp || 0),
          sellingPrice: Number(item.sellingPrice || lineRow?.sellingPrice || 0),
          freightCharges: 0,
          packingCharges: 0,
          totalPrice: breakup.lineGross,
          discountOnePercent: 0,
          discountOneAmount: 0,
          discountTwoPercent: 0,
          discountTwoAmount: 0,
          cashDiscountPercent: 0,
          cashDiscountAmount: 0,
          schemeDiscountPercent: 0,
          schemeDiscountAmount: 0,
          fixedDiscountPercent: 0,
          fixedDiscountAmount: 0,
          overAllDiscountAmount: 0,
          totalPriceWithoutGST: breakup.basicCostTotal,
          oldRoiPercent: item.oldRoiPercent, // fetch later
          newRoiPercent: +(
            ((item.mrp - item.costPrice) / item.mrp) *
            100
          ).toFixed(2),
          hsnCode: product?.hsnCode || '',
          id: item?.id,
          marginPercentage: Number(item.margin_percentage || item.marginPercentage || variant?.product?.marginPercentage || variant?.product?.margin_percentage || 0),
        };
      });

      // ✅ SANITIZE
      this.grnItems.forEach(item => this.sanitizeItemPricing(item));

      this.updateGrnSummary();

      if (gstChanges.length) {
        this.toastr.info(
          `GST rate updated from product master — ${gstChanges.join(', ')}`,
          'GST Rate Changed',
          { timeOut: 10000, extendedTimeOut: 5000 },
        );
      }
    } else {
      this.selectedPoNumber = null;
      this.selectedPoData = null;
      // Back to the header's active store/warehouse.
      this.documentLocationId = null;
      this.selectedVendorId = null;
      this.vendorDisabled = false;
      this.grnItems = [];
    }
  }

  async submitGrn(status: 'DRAFT' | 'POSTED' = 'POSTED') {
    // One save in flight at a time. The backend now also refuses a duplicate
    // vendor+invoice under a lock, but the second request must not even
    // leave the browser: on a dropped connection the FIRST one may still be
    // processed server-side after the client gave up on it.
    if (this.isSubmitting) return;

    // Light up inline "required" errors next to each empty mandatory field.
    this.submitAttempted = true;

    // Consolidated required-field check — one toast listing everything missing,
    // instead of three sequential dismissals. Mandatory: vendor, invoice date,
    // invoice number, invoice amount, ≥ 1 line item.
    const missing: string[] = [];
    if (!this.selectedVendorId) missing.push('Vendor');
    if (!this.invoiceDate) missing.push('Invoice Date');
    if (!this.invoiceNumber) missing.push('Invoice Number');
    if (!this.invoiceAmount) missing.push('Invoice Amount');
    if (!this.grnItems || this.grnItems.length === 0) missing.push('At least one product');
    // Amending a posted receipt must state why. Checked here so the reason
    // joins the same one-toast list as the other required fields; the server
    // rejects a reasonless amendment independently.
    if (this.grnStatus === 'POSTED' && !this.editReasonId) {
      missing.push('Amendment reason');
    }

    if (missing.length) {
      this.toastr.warning(
        `Please fill required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`,
        'Missing Information',
      );
      return;
    }

    if (status === 'POSTED') {
      if (this.hasOverReceipt()) {
        if (!this.authService.isAdmin()) {
          this.toastr.error(
            "Received quantity exceeds PO quantity on one or more items. You don't have the access to submit. Please save draft.",
            'Access Denied',
          );
          return;
        }
      }
    }
    this.isSaved = true;
    const totalQty = this.totalReceivedQuantity;
    const totalItems = this.totalProducts;

    // ✅ FINAL VALIDATION: Ensure MRP >= CP, SP >= CP, and SP <= MRP for all items
    // (equality allowed — zero-margin items can have SP = CP = MRP)
    // Compare at 3 decimals: CP is often derived (base × (1+tax)), and float
    // noise like 84.50000001 vs 84.5 must not fail an equal-looking pair.
    for (const item of this.grnItems) {
      const mrp = this.round3(+item.mrp || 0);
      const cp = this.round3(+item.costPrice || 0);
      const sp = this.round3(+item.sellingPrice || 0);

      if (mrp < cp) {
        this.toastr.error(`Item ${item.skuCode} has MRP (₹${mrp}) less than Cost Price (₹${cp}). MRP should be greater than or equal to Cost Price.`, 'Pricing Validation Error');
        return;
      }
      if (sp < cp) {
        this.toastr.error(`Item ${item.skuCode} has Selling Price (₹${sp}) less than Cost Price (₹${cp}). Selling Price should be greater than or equal to Cost Price.`, 'Pricing Validation Error');
        return;
      }
      if (sp > mrp) {
        this.toastr.error(`Item ${item.skuCode} has Selling Price (₹${sp}) greater than MRP (₹${mrp}). Selling Price can be same as MRP but not greater.`, 'Pricing Validation Error');
        return;
      }
    }

    // Amount/total mismatch — still a hard stop, but the "required" case was
    // already handled above so we only need the mismatch check here.
    const tolerance = environment.invoiceAmountTolerance ?? 0;
    if (Math.abs(this.invoiceAmount - this.grnSummary.finalTotal) > tolerance) {
      this.toastr.error(
        `Invoice amount(₹${this.invoiceAmount}) does not match Grand Total(₹${this.grnSummary.finalTotal}) (tolerance ±₹${tolerance})`,
        'Amount Mismatch'
      );
      return;
    }

    const grnRequest = {
      // Only meaningful when amending a POSTED GRN; ignored on drafts and
      // on the create path.
      editReasonId: this.editReasonId ?? undefined,
      editReasonNote: this.editReasonNote?.trim() || undefined,
      vendorId: this.selectedVendorId ?? null,
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      // The store/warehouse shown on the form (header context, or the linked
      // PO's own location). Omitted when unresolved — the backend then uses
      // the PO's store / active-store header.
      storeId: this.selectedWarehouse?.storeId ?? undefined,
      purchaseOrderId: this.selectedPoData?.id || null,
      ginId: this.selectedGinData?.id || null, // 🌟 Pass ginId here
      // Fall back to the IST calendar day, not `new Date()` — the raw instant
      // carries the UTC date, which is still yesterday before 05:30 IST and
      // would then be read as a backdated day by the server.
      // Omitted when received-date entry is off: the server stamps the
      // posting day and would discard the value anyway.
      receivedDate: this.allowReceivedDateEntry
        ? new Date(
            `${this.receivedDate || this.todayDate}T00:00:00Z`,
          ).toISOString()
        : undefined,
      invoiceDate: new Date(
        `${this.invoiceDate || this.todayDate}T00:00:00Z`,
      ).toISOString(),
      invoiceNumber: this.invoiceNumber,
      subTotalAmount: Number(this.grnSummary.subTotal),
      gstAmount: this.grnSummary.gstAmount,
      cessAmount: this.grnSummary.cessAmount,
      schemeDiscountAmount: this.grnSummary.schemeDiscountAmount,
      fixedDiscountAmount: this.grnSummary.fixedDiscountAmount,
      overallDiscountAmount: this.grnSummary.overallDiscountAmount,
      otherDiscountAmount: this.grnSummary.otherDiscounts,
      freightCharges: Number(this.freightCharges),
      packingCharges: Number(this.packingCharges),
      roundOffAmount: this.grnSummary.roundOff,
      otherCharges: this.grnSummary.otherCharges,
      totalAmount: this.grnSummary.finalTotal,
      status,
      totalQty,
      totalItems,
      costPriceIncludesGst: !!this.costPriceIncludesGst,

      items: this.grnItems.map((item) => {
        return {
          id: item.id || undefined,
          productVariantId: item.productVariantId,
          skuCode: item.skuCode,
          uom: item.uom,
          eanCode: item.eanCode,
          innerQuantity: Number(item.innerQuantity),
          poQuantity: Number(item.poQuantity),
          receivedQuantity: Number(item.receivedQuantity),
          freeQuantity: Number(item.freeQuantity),
          costPrice: Number(item.costPrice),
          actualCostPrice: Number(item.actualCostPrice),
          gstPercent: Number(item.gstPercent),
          gstAmount: Number(item.gstAmount),
          sgstPercent: Number(item.gstPercent) / 2,
          cgstPercent: Number(item.gstPercent) / 2,
          cessRate: Number(item.cessRate),
          cessAmount: Number(item.cessAmount),
          mrp: Number(item.mrp),
          sellingPrice: Number(item.sellingPrice),
          freightCharges: Number(item.freightCharges),
          packingCharges: 0,
          otherCharges: 0,
          totalPrice: Number(item.totalPrice),
          discountOnePercent: Number(item.discountOnePercent),
          discountOneAmount: Number(item.discountOneAmount),
          discountTwoPercent: Number(item.discountTwoPercent),
          discountTwoAmount: Number(item.discountTwoAmount),
          cashDiscountPercent: Number(item.cashDiscountPercent),
          cashDiscountAmount: Number(item.cashDiscountAmount),
          schemeDiscountPercent: Number(item.schemeDiscountPercent),
          schemeDiscountAmount: Number(item.schemeDiscountAmount),
          fixedDiscountPercent: Number(item.fixedDiscountPercent),
          fixedDiscountAmount: Number(item.fixedDiscountAmount),
          overAllDiscountAmount: Number(item.overAllDiscountAmount),
          oldRoiPercent: Number(item.oldRoiPercent),
          oldRoiAmount: 0,
          newRoiPercent: Number(item.newRoiPercent),
          newRoiAmount: 0,
          hsnCode: item.hsnCode,
          batchNumber: item.batchNumber || '',
          expiryDate: item.expiryDate
            ? new Date(item.expiryDate).toISOString()
            : null,
        };
      }),
    };

    // Final-submit confirmation. Drafts skip this — only ask before the
    // irreversible action that posts inventory changes. Uses the same
    // ConfirmDialogService used by the unsaved-changes guard.
    if (status === 'POSTED') {
      const proceed = await this.confirmDialog.confirm({
        title: this.grnId ? 'Update GRN?' : 'Submit GRN?',
        message: this.grnStatus === 'POSTED'
          // Already posted: only the difference against the receipt on record
          // moves, so a reduced quantity comes back OUT of stock.
          ? 'This GRN is already posted. Saving applies the difference between the quantities on record and the ones below to stock. Continue?'
          : this.grnId
            ? 'Are you sure you want to update and post this GRN? Inventory adjustments will be re-applied.'
            : 'Are you sure you want to submit this GRN? Once posted, inventory will be updated and this cannot be reversed.',
        confirmText: this.grnId ? 'Update' : 'Submit',
        cancelText: 'Cancel',
        size: 'lg',
      });
      if (!proceed) return;
    }

    const request$ = this.grnId
      ? this.grnService.updateGrn(this.grnId, grnRequest)
      : this.grnService.createGrn(grnRequest);

    this.isSubmitting = true;
    request$.pipe(finalize(() => (this.isSubmitting = false))).subscribe({
      next: async (res: any) => {
        const msg = this.grnId
          ? 'GRN updated successfully!'
          : 'GRN created successfully!';
        this.toastr.success(msg, 'Success');
        // On the server now; the browser copy has done its job.
        this.discardDraft();
        if (res?.grnNumber) this.grnNumber = res.grnNumber;
        // Remember the row we just created: if this screen is saved again
        // for any reason (preview failed to load, user pressed Submit once
        // more) it must UPDATE that GRN, never create a second one.
        if (!this.grnId && (res?.id || res?.data?.id)) {
          this.grnId = res.id || res.data.id;
          this.grnStatus = status;
        }

        // Partial receipt? Ask whether the source PO/GIN should stay open
        // for further GRNs or be closed (drops out of the GRN dropdown).
        if (status === 'POSTED') {
          await this.promptCloseSourceIfPartial();
        }

        // Trigger Preview for new GRNs (or upgrades if desired)
        // Check both res.id or res.data.id or fall back to this.grnId if update
        const createdGrnId = res?.id || res?.data?.id || this.grnId;

        if (createdGrnId) {
          this.loadPreview(createdGrnId);
        } else {
          this.afterSave();
        }
      },
      error: (err) => {
        const msg = this.grnId
          ? 'Failed to update GRN'
          : 'Failed to create GRN';
        // 409 = duplicate invoice number for this vendor (backend message
        // says so); anything else is a generic failure — don't misreport it.
        const serverMsg = err?.error?.message;
        if (String(err?.error?.code ?? '').startsWith('IDEMPOTENCY')) {
          // The first submit of this exact GRN is still running (or already
          // completed) on the server: nothing to re-enter, just look it up.
          this.toastr.info(serverMsg, 'Already submitted');
        } else if (err?.status === 409) {
          this.toastr.warning(
            serverMsg ||
              `Invoice Number: ${this.invoiceNumber} already exists for this vendor. Please enter a different invoice number.`,
            'Duplicate Invoice'
          );
        } else {
          this.toastr.error(serverMsg || msg, 'Error');
        }
      },
    });
  }

  /**
   * After posting a GRN raised against a PO or GIN, check the server's
   * pending-items for that source document. If quantities are still pending
   * (vendor short-shipped, or is sending the order across multiple
   * invoices/GRNs), ask the user whether to keep the document open for
   * further GRNs or close it. "Close" flips it to CLOSED on the backend so
   * it no longer appears in the GRN dropdowns; "Keep Open" (or dismissing
   * the dialog) changes nothing.
   */
  private async promptCloseSourceIfPartial(): Promise<void> {
    const ginId: string | null = this.selectedGinData?.id || null;
    const poId: string | null = this.selectedPoData?.id || null;
    if (!ginId && !poId) return; // direct GRN — nothing to close

    try {
      // Check the document the GRN was raised against (GIN wins when both
      // are set: a GIN-based GRN receives against the GIN's quantities).
      const response: any = await firstValueFrom(
        ginId
          ? this.grnService.getGinPendingItems(ginId)
          : this.poService.getPendingItems(poId!),
      );
      const pendingItems: any[] =
        response?.items || response?.data?.items || [];
      if (!pendingItems.length) return; // fully received — nothing to ask

      const poLabel = this.selectedPoNumber
        ? `PO ${this.selectedPoNumber}`
        : 'the PO';
      const docLabel = ginId
        ? `GIN ${this.selectedGinData?.ginNumber || ''}`.trim() +
          (poId ? ` (linked ${poLabel})` : '')
        : poLabel;

      const shouldClose = await this.confirmDialog.confirm({
        title: 'Items still pending — keep it open?',
        message:
          `This GRN does not cover all items on ${docLabel} — ` +
          `${pendingItems.length} item(s) are still pending. ` +
          `Keep it open if the vendor will send the remaining items later ` +
          `or is billing this order across multiple invoices (you can raise ` +
          `more GRNs against it). Close it if no more deliveries are ` +
          `expected — it will no longer appear in the GRN dropdown.`,
        confirmText: ginId ? 'Close GIN & PO' : 'Close PO',
        cancelText: 'Keep Open',
        size: 'lg',
      });
      if (!shouldClose) return; // keep open — the default, safe path

      const closeCalls: Promise<any>[] = [];
      if (ginId) closeCalls.push(firstValueFrom(this.grnService.closeGin(ginId)));
      if (poId) closeCalls.push(firstValueFrom(this.poService.closePo(poId)));
      await Promise.all(closeCalls);

      this.toastr.success(
        `${docLabel} closed. It will no longer appear in the GRN dropdown.`,
        'Closed',
      );
    } catch (err) {
      // Never block the GRN flow on this housekeeping step — the GRN itself
      // is already saved. Surface the failure and move on.
      console.error('[promptCloseSourceIfPartial] failed', err);
      this.toastr.warning(
        'Could not check/close the source PO/GIN. You can close it later from the PO page.',
        'Close skipped',
      );
    }
  }

  // Preview Logic
  showPreview = false;
  previewUrl: SafeResourceUrl | null = null;
  private rawBlobUrl: string | null = null;
  isLoadingPreview = false;
  sanitizer = inject(DomSanitizer);

  loadPreview(grnId: string) {
    this.showPreview = true;
    this.isLoadingPreview = true;

    this.grnService.downloadGrn(grnId).subscribe({
      next: (response: any) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          console.error('Empty PDF blob received');
          this.toastr.error('Received empty PDF');
          this.isLoadingPreview = false;
          return;
        }
        const url = URL.createObjectURL(blob);
        this.rawBlobUrl = url;
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          url + '#toolbar=0&navpanes=0&view=FitH',
        );
        this.isLoadingPreview = false;
      },
      error: (err) => {
        console.error('Failed to download GRN PDF', err);
        this.toastr.error('Failed to load preview');
        this.isLoadingPreview = false;
      },
    });
  }

  closePreview() {
    this.showPreview = false;
    // Clean up blob URL
    if (this.rawBlobUrl) {
      URL.revokeObjectURL(this.rawBlobUrl);
      this.rawBlobUrl = null;
    }
    this.afterSave();
  }

  /** Once a save is done (preview closed, or no preview): back to the GRN list. */
  private afterSave(): void {
    this.router.navigate(['/grn']);
  }

  downloadPdf() {
    if (!this.rawBlobUrl) return;
    const link = document.createElement('a');
    link.href = this.rawBlobUrl;
    link.download = `GRN-${this.grnNumber || 'document'}.pdf`;
    link.click();
  }

  printPdf() {
    if (!this.rawBlobUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.rawBlobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Optional: remove iframe after small delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }

  /**
   * Mirrors the PO equivalent. Both basicCostPrice (excl GST) and costPrice
   * (incl GST) are stored on variantPricing — neither is derived from the
   * other at runtime. The toggle chooses which one drives the line total to
   * match the vendor's invoice. actualCostPrice always equals the stored
   * basicCostPrice.
   */
  computeLineTaxBreakup(
    basicCostPrice: number,
    costPrice: number,
    qty: number,
    gstRate: number,
    cessRate: number = 0,
  ): {
    lineGross: number;
    gstAmount: number;
    cessAmount: number;
    basicCostTotal: number;
    actualCostPrice: number;
  } {
    const r2 = (n: number) => +(+n).toFixed(2);
    // r3: price-derived line totals keep 3 decimals so a CP like 85.501
    // survives into Cost Price Total / Basic Cost Price Total instead of
    // showing 85.50 while validation sees 85.501. Tax amounts stay at 2
    // decimals (invoice/accounting convention).
    const r3 = (n: number) => +(+n).toFixed(3);
    // r4: per-unit rate retains 4 decimals so derived per-base-unit prices
    // (e.g., per-kg from per-bag) don't lose precision once multiplied by qty.
    const r4 = (n: number) => +(+n).toFixed(4);
    const bp = Number(basicCostPrice) || 0;
    const cp = Number(costPrice) || 0;
    const q = Number(qty) || 0;
    const g = Number(gstRate) || 0;
    const c = Number(cessRate) || 0;
    const totalRate = g + c;
    const inclusive = !!this.costPriceIncludesGst;

    if (inclusive) {
      const lineGross = r3(cp * q);
      const gstAmount = r2((lineGross * g) / (100 + totalRate));
      const cessAmount = r2((lineGross * c) / (100 + totalRate));
      const basicCostTotal = r3(lineGross - gstAmount - cessAmount);
      return { lineGross, gstAmount, cessAmount, basicCostTotal, actualCostPrice: r4(bp) };
    }

    const basicCostTotal = r3(bp * q);
    const gstAmount = r2((basicCostTotal * g) / 100);
    const cessAmount = r2((basicCostTotal * c) / 100);
    const lineGross = r3(basicCostTotal + gstAmount + cessAmount);
    return { lineGross, gstAmount, cessAmount, basicCostTotal, actualCostPrice: r4(bp) };
  }

  /**
   * Basic cost (excl GST) for a line, consistent with its cost price: the
   * price row matching the cost, then the stored line value — each only if
   * it agrees with the cost — else derived from the cost.
   */
  private resolveBasicCostPrice(item: any): number {
    const cost = Number(item?.costPrice ?? 0);
    const gst = Number(item?.gstPercent ?? 0);
    const cess = Number(item?.cessRate ?? item?.cessPercent ?? 0);
    const rows = item?.productVariant?.pricing ?? item?.pricing;
    return consistentBasicCost(
      cost,
      gst + cess,
      pricingForCost(rows, cost)?.basePrice,
      item?.actualCostPrice ?? item?.basicCostPrice,
    );
  }

  /** Recompute every line item when the Cost Basis toggle flips. */
  onCostPriceModeChange(): void {
    for (const item of this.grnItems) {
      this.onPriceChange(item, '');
    }
    this.updateGrnSummary();
  }

  onPriceChange(item: any, changedKey: string) {
    const unitPrice = +item.costPrice || 0;
    const gstPercent = +item.gstPercent || 0;
    const cessPercent = +item.cessPercent || +item.cessRate || 0;
    const qty = +item.receivedQuantity || item.quantity || 0;
    const freight = +item.freightCharges || 0;

    // Stored basic cost (excl GST) — independent of the toggle. The toggle
    // only changes which one (basic or inclusive) drives the line total.
    const basicCostPrice = this.resolveBasicCostPrice(item);
    item.actualCostPrice = basicCostPrice;
    item.basicCostPrice = basicCostPrice;

    const breakup = this.computeLineTaxBreakup(
      basicCostPrice,
      unitPrice,
      qty,
      gstPercent,
      cessPercent,
    );
    // Per-unit basic cost price (excl GST). Line-item discounts are applied on
    // the Basic Cost Price, and GST/CESS are charged on the net (post-discount)
    // taxable value — the standard GST treatment.
    const basic = breakup.actualCostPrice; // per-unit (= stored basicCostPrice)

    // Handle % based to amount — percentages are taken on the Basic Cost Price.
    if (changedKey === 'discountOnePercent') {
      item.discountOneAmount = +(
        (basic * item.discountOnePercent) /
        100
      ).toFixed(2);
    } else if (changedKey === 'discountOneAmount') {
      item.discountOnePercent = basic
        ? +((item.discountOneAmount / basic) * 100).toFixed(2)
        : 0;
    }

    if (changedKey === 'discountTwoPercent') {
      item.discountTwoAmount = +(
        (basic * item.discountTwoPercent) /
        100
      ).toFixed(2);
    } else if (changedKey === 'discountTwoAmount') {
      item.discountTwoPercent = basic
        ? +((item.discountTwoAmount / basic) * 100).toFixed(2)
        : 0;
    }

    if (changedKey === 'cashDiscountPercent') {
      item.cashDiscountAmount = +(
        (basic * item.cashDiscountPercent) /
        100
      ).toFixed(2);
    } else if (changedKey === 'cashDiscountAmount') {
      item.cashDiscountPercent = basic
        ? +((item.cashDiscountAmount / basic) * 100).toFixed(2)
        : 0;
    }

    // Scheme & Fixed discounts are flat per-line amounts (not per-unit), so
    // their percentage is taken on the line's gross Basic Cost total.
    const lineBasicTotal = +breakup.basicCostTotal || 0;
    if (changedKey === 'schemeDiscountPercent') {
      item.schemeDiscountAmount = +(
        (lineBasicTotal * (+item.schemeDiscountPercent || 0)) /
        100
      ).toFixed(2);
    } else if (changedKey === 'schemeDiscountAmount') {
      item.schemeDiscountPercent = lineBasicTotal
        ? +(((+item.schemeDiscountAmount || 0) / lineBasicTotal) * 100).toFixed(2)
        : 0;
    }

    if (changedKey === 'fixedDiscountPercent') {
      item.fixedDiscountAmount = +(
        (lineBasicTotal * (+item.fixedDiscountPercent || 0)) /
        100
      ).toFixed(2);
    } else if (changedKey === 'fixedDiscountAmount') {
      item.fixedDiscountPercent = lineBasicTotal
        ? +(((+item.fixedDiscountAmount || 0) / lineBasicTotal) * 100).toFixed(2)
        : 0;
    }

    // Ensure scheme/fixed discounts exist
    item.schemeDiscountPercent = +item.schemeDiscountPercent || 0;
    item.fixedDiscountPercent = +item.fixedDiscountPercent || 0;
    item.schemeDiscountAmount = +item.schemeDiscountAmount || 0;
    item.fixedDiscountAmount = +item.fixedDiscountAmount || 0;

    // Handle manual change
    if (
      changedKey === 'schemeDiscountAmount' ||
      changedKey === 'fixedDiscountAmount' ||
      changedKey === 'overAllDiscountAmount'
    ) {
      item.schemeDiscountAmount = +item.schemeDiscountAmount;
      item.fixedDiscountAmount = +item.fixedDiscountAmount;
    }

    const disc1Total = +(item.discountOneAmount * qty).toFixed(2);
    const disc2Total = +(item.discountTwoAmount * qty).toFixed(2);
    const cashDiscTotal = +(item.cashDiscountAmount * qty).toFixed(2);
    const schemeDiscount = +item.schemeDiscountAmount;
    const fixedDiscount = +item.fixedDiscountAmount;

    const totalDiscount =
      disc1Total + disc2Total + cashDiscTotal + schemeDiscount + fixedDiscount;

    // Net taxable base = gross Basic Cost (qty × per-unit basic) minus ALL
    // discounts. GST and CESS are recomputed on this discounted value.
    const netBasicTotal = +Math.max(
      0,
      breakup.basicCostTotal - totalDiscount,
    ).toFixed(3);
    const gstAmount = +((netBasicTotal * gstPercent) / 100).toFixed(2);
    const cessAmount = +((netBasicTotal * cessPercent) / 100).toFixed(2);

    item.basicCostPrice = basic;
    item.basicCostTotal = breakup.basicCostTotal; // gross basic (pre-discount)
    item.gstAmount = gstAmount;
    item.cessAmount = cessAmount;
    item.overAllDiscountAmount = +totalDiscount.toFixed(2);

    // Sub-total without GST = net basic (post-discount) + freight.
    item.totalPriceWithoutGST = +(netBasicTotal + freight).toFixed(3);

    // Line total = net basic + GST + CESS (both charged on the net base) + freight.
    item.totalPrice = +(
      netBasicTotal +
      gstAmount +
      cessAmount +
      freight
    ).toFixed(3);

    this.updateGrnSummary();
  }

  updateGrnSummary() {
    let subTotal = 0;
    let gst = 0;
    let cess = 0;
    let scheme = 0;
    let fixed = 0;
    let other = 0;

    let freight = +this.freightCharges || 0;
    let packing = +this.packingCharges || 0;
    let otherCharges = 0;

    let lineItemTotalDiscount = 0;

    for (const item of this.grnItems) {
      // Trust the values onPriceChange / breakup put on the item — do NOT
      // recompute here, otherwise we clobber the toggle-aware math.
      const basicTotal = +item.basicCostTotal;
      const gstAmt = +item.gstAmount || 0;
      const cessAmt = +item.cessAmount || 0;
      const qty = +item.receivedQuantity || 0;

      // Legacy items (loaded from old data) may not have basicCostTotal
      // stored — fall back to per-unit basic × qty so they still aggregate.
      const lineTaxable = !isNaN(basicTotal) && basicTotal > 0
        ? basicTotal
        : (+item.actualCostPrice || 0) * qty;

      subTotal += lineTaxable;
      gst += gstAmt;
      cess += cessAmt;

      scheme += +item.schemeDiscountAmount || 0;
      fixed += +item.fixedDiscountAmount || 0;
      other +=
        (+item.discountOneAmount || 0) +
        (+item.discountTwoAmount || 0) +
        (+item.cashDiscountAmount || 0);

      lineItemTotalDiscount += +item.overAllDiscountAmount || 0;
    }

    const totalBeforeTax = subTotal;
    const overallDiscount = +this.grnSummary?.otherDiscounts || 0;
    const overAllDiscountAmount = lineItemTotalDiscount + overallDiscount;

    // Line-item discounts (Disc1/Disc2/Cash/Scheme/Fixed) reduce the taxable
    // base — surfaced in the summary as: Sub Total (gross) − Discount = Taxable
    // Value, with GST charged on the Taxable Value.
    const lineDiscountAmount = +lineItemTotalDiscount.toFixed(2);
    const taxableValue = +(subTotal - lineDiscountAmount).toFixed(2);

    const grand =
      subTotal +
      gst +
      cess +
      otherCharges +
      freight +
      packing -
      overAllDiscountAmount;

    const roundOff = +(Math.round(grand) - grand).toFixed(2);
    const finalTotal = Math.round(grand);

    this.grnSummary = {
      subTotal: totalBeforeTax,
      lineDiscountAmount,
      taxableValue,
      gstAmount: gst,
      cessAmount: cess,
      schemeDiscountAmount: scheme,
      fixedDiscountAmount: fixed,
      otherDiscounts: overallDiscount,
      overallDiscountAmount: overAllDiscountAmount,
      freightCharges: freight,
      packingCharges: packing,
      otherCharges,
      grandTotal: grand,
      roundOff,
      finalTotal,
    };
  }

  /**
   * Unit weight (kg) for a line, resolved from whatever product/variant data
   * the row carries. Used only for relative freight apportionment, so absolute
   * accuracy matters less than using a consistent source across rows.
   */
  private getItemUnitWeight(item: any): number {
    return (
      +item?.weightKg ||
      +item?.productVariant?.weight ||
      +item?.productVariant?.product?.weightKg ||
      +item?.product?.weightKg ||
      0
    );
  }

  /**
   * Distribute the header-level Freight Charges across line items in proportion
   * to their total weight (unit weight × received qty) — the standard retail
   * ERP landed-cost method: heavier lines absorb proportionally more freight.
   *
   * Fallbacks when no weights are captured: apportion by line value, then an
   * equal split, so freight is never silently dropped. Rounding drift is pushed
   * onto the heaviest line so per-line shares sum exactly to the entered
   * freight. The GRN grand total still adds the header freight once (the
   * summary is built from component amounts, not line landed totals), so this
   * does not double-count.
   */
  apportionFreightByWeight(): void {
    const items = this.grnItems || [];
    if (!items.length) return;

    const totalFreight = +this.freightCharges || 0;

    // Primary basis: weight × received qty.
    let basis = items.map(
      (it) => this.getItemUnitWeight(it) * (+it.receivedQuantity || 0),
    );
    let totalBasis = basis.reduce((a, b) => a + b, 0);

    // Fallback 1: apportion by line value.
    if (totalBasis <= 0) {
      basis = items.map(
        (it) =>
          +it.basicCostTotal ||
          (+it.actualCostPrice || 0) * (+it.receivedQuantity || 0),
      );
      totalBasis = basis.reduce((a, b) => a + b, 0);
    }
    // Fallback 2: equal split.
    if (totalBasis <= 0) {
      basis = items.map(() => 1);
      totalBasis = items.length;
    }

    let allocated = 0;
    let maxIdx = 0;
    items.forEach((it, i) => {
      if (basis[i] > basis[maxIdx]) maxIdx = i;
      if (totalFreight <= 0 || totalBasis <= 0) {
        it.freightCharges = 0;
        return;
      }
      const share = +((totalFreight * basis[i]) / totalBasis).toFixed(2);
      it.freightCharges = share;
      allocated += share;
    });

    // Absorb rounding drift on the heaviest line so the shares sum exactly.
    if (totalFreight > 0) {
      const drift = +(totalFreight - allocated).toFixed(2);
      if (drift !== 0) {
        items[maxIdx].freightCharges = +(
          (+items[maxIdx].freightCharges || 0) + drift
        ).toFixed(2);
      }
    }

    // Recompute each line so the landed totals reflect the new freight share.
    items.forEach((it) => this.onPriceChange(it, ''));
    this.updateGrnSummary();
  }

  onFreightChange(): void {
    this.apportionFreightByWeight();
  }

  onReceivedQtyChange(_item?: any): void {
    // Qty drives both the line totals and the weight basis, so re-apportion
    // freight across all lines (this also recomputes every line + summary).
    this.apportionFreightByWeight();
  }

  onVendorChange() {
    if (!this.selectedPoData) {
      this.grnItems = [];
    }
    // Clear stale product-search results so a vendor switch doesn't leave
    // the previous vendor's items visible in the dropdowns.
    this.products = [];
    this.searchResults = [];
    this.eanSearchResults = [];
    this.selectedProduct = null;
    this.selectedVariant = null;
    // The "Add products" dialog re-queries on open, so nothing to preload.
  }

  /**
   * Fetch the first 5 products linked to the currently selected vendor
   * (GRN context) and populate both manual-search dropdowns. No-op when
   * no vendor is selected. Results are replaced by the user's own typed
   * search via the productSearch$ / productCodeSearch$ pipes.
   */
  private preloadVendorProducts(): void {
    if (!this.selectedVendorId) return;

    this.productService
      .searchProducts({
        context: 'GRN',
        vendorId: this.selectedVendorId,
        locationId: this.searchLocationId,
        limit: 5,
      })
      .pipe(
        catchError((err) => {
          console.error('Failed to preload vendor products', err);
          return of({ data: [], total: 0 });
        }),
      )
      .subscribe((response: any) => {
        const list: any[] = response?.data || [];
        // Name-search dropdown — bindLabel="longDescription"
        this.products = list;
        // SKU-search dropdown — bindLabel="sku", needs displayText
        this.searchResults = list.map((item: any) => ({
          ...item,
          sku: item.sku || item.skuCode,
          ean: currentEanOf(item.variants?.[0]),
          displayText: `${item.sku || item.skuCode} - ${item.longDescription} `,
        }));
        // EAN dropdown — only items that actually carry a code.
        this.eanSearchResults = withMatchedEan(list, '').filter(
          (item: any) => !!item.matchedEan,
        );
      });
  }

  customSearchFn(term: string, item: any): boolean {
    term = term.toLowerCase();
    return (
      item?.longDescription?.toLowerCase()?.includes(term) ||
      item?.skuCode?.toLowerCase()?.includes(term)
    );
  }

  onProductSelected(product: any) {
    this.selectedProduct = product;
    console.log(`[GRN Product Select] Product: ${product.longDescription}, Margin: ${product.marginPercentage || product.margin_percentage}%, GST: ${product.purchaseGstRate?.value || product.gstPercent}%`);
    this.selectedVariant = product?.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.selectedMRP = +this.selectedVariant?.pricing?.[0]?.mrp || 0;
    this.warnIfUnpriced(product, this.selectedVariant);
    this.populatePriceOptions(product);

    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    });
  }

  /**
   * A SKU can be created with just a barcode and priced later — typically at
   * this very GRN. Say so up front instead of silently prefilling ₹0.
   */
  private warnIfUnpriced(product: any, variant: any) {
    if (!product || hasPurchasePrice(variant)) return;
    this.toastr.warning(
      priceNotSetMessage(product.sku, product.longDescription),
      'Price required',
    );
  }

  confirmAddToTable() {
    if (
      !this.selectedProduct ||
      !this.selectedQuantity ||
      !this.enteredCostPrice
    )
      return;

    // One SKU may appear on several lines as long as the prices differ (the
    // same delivery can carry old-MRP and new-MRP stock). Only an exact
    // SKU + cost price repeat is stopped — warn and jump to that row so the
    // user edits its quantity instead of creating an identical line.
    const dupIndex = this.grnItems.findIndex(
      (item) =>
        item.skuCode === this.selectedProduct?.sku &&
        +item.costPrice === +this.enteredCostPrice,
    );
    if (dupIndex !== -1) {
      this.toastr.warning(
        `${this.selectedProduct?.sku} is already in the list at this cost price.`,
        'Item already added',
      );
      this.selectedProduct = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.selectedMRP = 0;
      setTimeout(() => {
        const el = document.getElementById('qty-' + dupIndex) as HTMLInputElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
          el.select();
        }
      }, 100);
      return;
    }

    const product = this.selectedProduct;
    const variant = this.selectedVariant;
    const qty = this.selectedQuantity;
    const costPrice = +this.enteredCostPrice;
    const mrp = +this.selectedMRP || 0;

    this.ensureVariantPricing(variant, costPrice, mrp).subscribe((pricingList) => {
      const targetIndex = this.addOrMergeGrnLine(product, variant, qty, costPrice, mrp, pricingList, true);

      this.selectedProduct = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.selectedMRP = 0;
      // Re-distribute freight now that a line was added/merged.
      this.apportionFreightByWeight();

      // ✅ Focus logic: jump to the updated or newly added row in the table
      setTimeout(() => {
        const element = document.getElementById('qty-' + targetIndex);
        if (element) {
          element.focus();
          (element as HTMLInputElement).select();
        }
      }, 100);
    });
  }

  /**
   * Make sure `variant.pricing` carries a row for this cost price. A new CP
   * is persisted first (seeded from the row whose MRP the user picked, not
   * blindly from the newest — otherwise the selected MRP is replaced) and
   * prepended to the variant's pricing list. Emits the up-to-date list.
   */
  private ensureVariantPricing(variant: any, costPrice: number, mrp: number): Observable<any[]> {
    const pricing: any[] = variant?.pricing || [];
    const existing = pricing.find((p: any) => +p.costPrice === +costPrice);
    if (existing) return of(pricing);

    const seed = pricing.find((p: any) => +p.mrp === +(mrp || 0)) || pricing[0] || {};
    const body: any = {
      ...seed,
      productVariantId: seed.productVariantId || variant?.id,
      costPrice: +costPrice,
      basePrice: +costPrice,
    };
    delete body.id;
    delete body.createdAt;
    delete body.updatedAt;
    body.mrp = Number(mrp || body.mrp || 0);
    body.sellingPrice = Number(body.sellingPrice || 0);
    return this.productService.addVariantPricing(body).pipe(
      map(() => {
        if (!variant.pricing) variant.pricing = [];
        variant.pricing.unshift(body);
        return variant.pricing as any[];
      }),
    );
  }

  /**
   * Add a received line for `product`/`variant`, or merge the quantity into an
   * existing line with the same SKU + cost price. Shared by the single-product
   * strip and the multi-select "Add products" dialog. Returns the row index.
   */
  private addOrMergeGrnLine(
    product: any,
    variant: any,
    quantity: number,
    costPrice: number,
    selectedMrp: number,
    pricingList: any[],
    toastOnMerge = true,
  ): number {
    const gstRate = parseFloat(product?.purchaseGstRate?.value || '0');
    const cessRate = +product?.cessRate || 0;
    // Honor the user's CP+MRP pair: two pricing rows can share a cost price
    // with different MRPs, so match on both before falling back to CP-only,
    // then newest.
    const matchedPricing =
      pricingList.find((p: any) => +p.costPrice === +costPrice && +p.mrp === selectedMrp) ||
      pricingList.find((p: any) => +p.costPrice === +costPrice) ||
      pricingList[0];
    const basicCostPrice = consistentBasicCost(
      costPrice,
      gstRate + cessRate,
      pricingForCost(pricingList, costPrice)?.basePrice,
      matchedPricing?.basePrice,
    );
    const breakup = this.computeLineTaxBreakup(basicCostPrice, costPrice, quantity, gstRate, cessRate);
    const actualCost = breakup.actualCostPrice;
    const gstAmount = breakup.gstAmount;
    const totalWithGST = breakup.lineGross;

    // ✅ MERGE LOGIC: check if item with same SKU and CP already exists
    const existingTableIndex = this.grnItems.findIndex(
      (item) => item.skuCode === product?.sku && +item.costPrice === +costPrice,
    );

    if (existingTableIndex !== -1) {
      const existingItem = this.grnItems[existingTableIndex];
      existingItem.receivedQuantity = Number(existingItem.receivedQuantity || 0) + quantity;
      existingItem.poQuantity = Number(existingItem.poQuantity || 0) + quantity;
      existingItem.gstAmount = +(Number(existingItem.gstAmount || 0) + gstAmount).toFixed(2);
      existingItem.totalPrice = +(Number(existingItem.totalPrice || 0) + totalWithGST).toFixed(3);
      existingItem.totalPriceWithoutGST = +(Number(existingItem.totalPriceWithoutGST || 0) + breakup.basicCostTotal).toFixed(3);
      if (toastOnMerge) {
        this.toastr.info(`Updated quantity for ${existingItem.skuCode}`, 'Item Merged');
      }
      return existingTableIndex;
    }

    this.grnItems.push({
      productVariant: variant,
      productVariantId: variant.id,
      skuCode: product?.sku || '',
      pluCode: '',
      description: product?.longDescription || '',
      eanCode: currentEanOf(variant),
      weightKg: +product?.weightKg || +variant?.weight || 0,
      innerQuantity: product?.innerPackaging || 1,
      uom: product?.uom || '',
      // Decide whether this line's quantity box accepts decimals: a kg/gm/ltr
      // unit, or a bulk / weighable / bulk-child product.
      isBulk: product?.isBulk ?? null,
      isWeighable: product?.isWeighable ?? null,
      bulkParentProductId: product?.bulkParentProductId ?? null,
      poQuantity: quantity,
      receivedQuantity: quantity,
      freeQuantity: 0,
      actualCostPrice: actualCost,
      basicCostPrice,
      gstPercent: gstRate,
      gstAmount,
      costPrice,
      mrp: Number(selectedMrp || matchedPricing?.mrp || 0),
      sellingPrice: Number(matchedPricing?.sellingPrice || 0),
      marginPercentage: product?.marginPercentage || product?.margin_percentage || 0,
      freightCharges: 0,
      packingCharges: 0,
      totalPrice: totalWithGST,
      discountOnePercent: 0,
      discountOneAmount: 0,
      discountTwoPercent: 0,
      discountTwoAmount: 0,
      cashDiscountPercent: 0,
      cashDiscountAmount: 0,
      schemeDiscountPercent: 0,
      schemeDiscountAmount: 0,
      fixedDiscountPercent: 0,
      fixedDiscountAmount: 0,
      overAllDiscountAmount: 0,
      oldRoiPercent: 0,
      newRoiPercent: 0,
      hsnCode: product?.hsnCode || '',
      cessRate: cessRate,
      cessAmount: breakup.cessAmount,
      basicCostTotal: breakup.basicCostTotal,
      totalPriceWithoutGST: breakup.basicCostTotal,
    });
    const targetIndex = this.grnItems.length - 1;
    this.sanitizeItemPricing(this.grnItems[targetIndex]);
    return targetIndex;
  }

  // ── "Add products" dialog ──────────────────────────────────────────────
  // Replaces the old "Add manually" toggle + single-product strip. One search
  // box (name / SKU / PLU / EAN, or a scanner), a result sheet where each row
  // carries its own qty / cost price / MRP, and selections that survive
  // further searches so a whole delivery can be picked and added in one go.

  pickerOpen = false;
  pickerQuery = '';
  pickerSearch$ = new Subject<string>();
  pickerResults: GrnPickerRow[] = [];
  pickerLoading = false;
  pickerAdding = false;
  pickerPage = 1;
  pickerTotal = 0;
  readonly pickerPageSize = 30;
  /** Scope the search to the selected vendor (default on when one is chosen). */
  pickerVendorOnly = true;
  pickerView: 'results' | 'selected' = 'results';
  /** Selected rows keyed by variant id — kept across searches. */
  pickerSelected = new Map<string, GrnPickerRow>();
  @ViewChild('pickerInput') pickerInputRef?: ElementRef<HTMLInputElement>;
  private pickerSearchSub?: Subscription;

  /** Attribute filters: department → category → sub-category, manufacturer → brand. */
  pickerFilters = {
    departmentId: null as string | null,
    categoryId: null as string | null,
    subCategoryId: null as string | null,
    manufacturerId: null as string | null,
    brandId: null as string | null,
    inStockOnly: false,
    hideAdded: false,
  };
  pickerDepartments: any[] = [];
  pickerCategories: any[] = [];
  pickerSubCategories: any[] = [];
  pickerManufacturers: any[] = [];
  private pickerAllBrands: any[] = [];
  pickerBrands: any[] = [];
  private pickerLookupsLoaded = false;

  get pickerFilterCount(): number {
    const f = this.pickerFilters;
    return [f.departmentId, f.categoryId, f.subCategoryId, f.manufacturerId, f.brandId].filter(Boolean).length
      + (f.inStockOnly ? 1 : 0) + (f.hideAdded ? 1 : 0);
  }

  get pickerRows(): GrnPickerRow[] {
    if (this.pickerView === 'selected') return Array.from(this.pickerSelected.values());
    return this.pickerFilters.hideAdded
      ? this.pickerResults.filter((r) => !r.inList || r.selected)
      : this.pickerResults;
  }

  get pickerSelectedUnits(): number {
    let n = 0;
    this.pickerSelected.forEach((r) => (n += +r.qty || 0));
    return n;
  }

  get pickerSelectedValue(): number {
    let v = 0;
    this.pickerSelected.forEach((r) => (v += (+r.qty || 0) * (+(r.costPrice ?? 0) || 0)));
    return v;
  }

  get pickerUnpricedCount(): number {
    let n = 0;
    this.pickerSelected.forEach((r) => { if (!(+(r.costPrice ?? 0) > 0)) n++; });
    return n;
  }

  get pickerHasMore(): boolean {
    return this.pickerResults.length < this.pickerTotal;
  }

  /**
   * Open the dialog. `preselect` seeds it with one product (used when a
   * scanned item has no price yet, so the operator keys the cost here).
   */
  openProductPicker(preselect?: any) {
    this.pickerOpen = true;
    this.pickerView = 'results';
    this.pickerVendorOnly = !!this.selectedVendorId;
    this.loadPickerLookups();
    if (!this.pickerSearchSub) {
      this.pickerSearchSub = this.pickerSearch$
        .pipe(
          debounceTime(250),
          // A new search or filter is about results: leave the "Selected"
          // view (a failed "Add lines" switches to it), or the sheet keeps
          // showing the selection and the filters look dead.
          tap(() => (this.pickerView = 'results')),
          switchMap((term) => this.fetchPickerPage(term, 1)),
        )
        .subscribe((res) => this.applyPickerPage(res, true));
    }
    if (preselect) {
      const row = this.toPickerRow(preselect);
      this.pickerQuery = row.sku;
      this.pickerResults = [row];
      this.pickerTotal = 1;
      this.pickerToggle(row, true);
      setTimeout(() => {
        const el = document.getElementById('pk-cp-' + row.key) as HTMLInputElement | null;
        el?.focus();
      }, 50);
    } else {
      this.pickerQuery = '';
      this.pickerRefresh();
      setTimeout(() => this.pickerInputRef?.nativeElement?.focus(), 50);
    }
  }

  closeProductPicker() {
    this.pickerOpen = false;
  }

  /** Re-run the current query from page 1 (query / vendor-scope change). */
  pickerRefresh() {
    this.pickerSearch$.next(this.pickerQuery || '');
  }

  pickerLoadMore() {
    if (!this.pickerHasMore || this.pickerLoading) return;
    this.fetchPickerPage(this.pickerQuery, this.pickerPage + 1).subscribe((res) =>
      this.applyPickerPage(res, false),
    );
  }

  private fetchPickerPage(term: string, page: number): Observable<{ data: any[]; total: number; page: number }> {
    this.pickerLoading = true;
    const query = (term || '').trim();
    const f = this.pickerFilters;
    return this.productService
      .searchProducts({
        context: 'GRN',
        query: query || undefined,
        vendorId: this.pickerVendorOnly && this.selectedVendorId ? this.selectedVendorId : undefined,
        locationId: this.searchLocationId,
        departmentId: f.departmentId || undefined,
        categoryId: f.categoryId || undefined,
        subCategoryId: f.subCategoryId || undefined,
        manufacturerId: f.manufacturerId || undefined,
        brandId: f.brandId || undefined,
        onlyInStock: f.inStockOnly && !!this.searchLocationId ? true : undefined,
        page,
        limit: this.pickerPageSize,
      })
      .pipe(
        catchError(() => of({ data: [], total: 0 })),
        map((res: any) => ({ data: res?.data || [], total: +res?.total || 0, page })),
      );
  }

  private applyPickerPage(res: { data: any[]; total: number; page: number }, replace: boolean) {
    this.pickerLoading = false;
    this.pickerPage = res.page;
    this.pickerTotal = res.total;
    // Reuse the selected row object so qty / price edits stay in sync between
    // the results sheet and the "Selected" view.
    const rows = res.data
      .map((p) => this.toPickerRow(p))
      .map((r) => this.pickerSelected.get(r.key) || r);
    this.pickerResults = replace ? rows : [...this.pickerResults, ...rows];
    this.cdr.detectChanges();
  }

  private toPickerRow(product: any): GrnPickerRow {
    const variant = product?.variants?.[0] || {};
    const pricing: any[] = variant.pricing || [];
    // A ₹0 cost row (SKU priced for sale only) is not a purchase price: it is
    // left out of the select, and with no real cost on file the cost is typed
    // — otherwise the only choice is 0 and "Add lines" can never pass.
    const costOptions = pricing.filter((p: any) => +p.costPrice > 0);
    const first = costOptions[0];
    const sku = product?.sku || product?.skuCode || '';
    const inList = this.grnItems.some((i) => i.skuCode === sku);
    return {
      key: variant.id || sku,
      product,
      variant,
      sku,
      description: product?.longDescription || product?.description || '',
      ean: currentEanOf(variant) || '',
      uom: product?.uom || '',
      pricing,
      costOptions,
      freeCost: !costOptions.length,
      costPrice: first ? +first.costPrice : null,
      mrp: +(first ?? pricing.find((p: any) => +p.mrp > 0))?.mrp || 0,
      qty: product?.innerPackaging || 1,
      allowDecimal: this.allowsDecimal(product),
      unpriced: !hasPurchasePrice(variant),
      inList,
      selected: false,
    };
  }

  /** Departments, manufacturers and brands are fetched once per page visit. */
  private loadPickerLookups() {
    if (this.pickerLookupsLoaded) return;
    this.pickerLookupsLoaded = true;
    this.productService.getAllDepartments().subscribe({
      next: (res: any) => (this.pickerDepartments = (res?.data || res || []).filter((d: any) => d.isActive !== false)),
      error: () => (this.pickerLookupsLoaded = false),
    });
    this.poService.getManufacturers().subscribe({
      next: (res: any) => (this.pickerManufacturers = (res?.data || res || []).filter((m: any) => m.isActive !== false)),
      error: () => (this.pickerLookupsLoaded = false),
    });
    this.manufacturerService.getAllManufacturersBrands().subscribe({
      next: (res: any) => {
        this.pickerAllBrands = (res?.data || res || []).filter((b: any) => b.isActive !== false);
        this.pickerBrands = this.pickerAllBrands;
      },
      error: () => (this.pickerLookupsLoaded = false),
    });
  }

  /** Brand dropdown search matches the brand or its manufacturer. */
  pickerBrandSearch(term: string, item: any): boolean {
    const t = (term || '').toLowerCase();
    return (
      (item?.name || '').toLowerCase().includes(t) ||
      (item?.manufacturer?.name || '').toLowerCase().includes(t)
    );
  }

  pickerDepartmentChanged() {
    const f = this.pickerFilters;
    f.categoryId = null;
    f.subCategoryId = null;
    this.pickerCategories = [];
    this.pickerSubCategories = [];
    if (f.departmentId) {
      this.productService.getCategoriesByDepartment(f.departmentId).subscribe((res: any) => {
        this.pickerCategories = res?.data || res || [];
      });
    }
    this.pickerRefresh();
  }

  pickerCategoryChanged() {
    const f = this.pickerFilters;
    f.subCategoryId = null;
    this.pickerSubCategories = [];
    if (f.categoryId) {
      this.productService.getSubCategories(f.categoryId).subscribe((res: any) => {
        this.pickerSubCategories = res?.data || res || [];
      });
    }
    this.pickerRefresh();
  }

  pickerManufacturerChanged() {
    const f = this.pickerFilters;
    this.pickerBrands = f.manufacturerId
      ? this.pickerAllBrands.filter((b) => b.manufacturerId === f.manufacturerId)
      : this.pickerAllBrands;
    if (f.brandId && !this.pickerBrands.some((b) => b.id === f.brandId)) f.brandId = null;
    this.pickerRefresh();
  }

  /** Picking a brand without a manufacturer implies its manufacturer. */
  pickerBrandChanged() {
    const f = this.pickerFilters;
    const b = this.pickerAllBrands.find((x) => x.id === f.brandId);
    if (b && !f.manufacturerId && b.manufacturerId) {
      // The manufacturer list may not hold this one (it is not the full
      // list); without it the select shows a blank value.
      if (b.manufacturer && !this.pickerManufacturers.some((m) => m.id === b.manufacturerId)) {
        this.pickerManufacturers = [...this.pickerManufacturers, b.manufacturer];
      }
      f.manufacturerId = b.manufacturerId;
      this.pickerBrands = this.pickerAllBrands.filter((x) => x.manufacturerId === b.manufacturerId);
    }
    this.pickerRefresh();
  }

  pickerClearFilters() {
    this.pickerFilters = {
      departmentId: null, categoryId: null, subCategoryId: null,
      manufacturerId: null, brandId: null, inStockOnly: false, hideAdded: false,
    };
    this.pickerCategories = [];
    this.pickerSubCategories = [];
    this.pickerBrands = this.pickerAllBrands;
    this.pickerRefresh();
  }

  pickerToggle(row: GrnPickerRow, force?: boolean) {
    const on = force ?? !row.selected;
    row.selected = on;
    if (on) this.pickerSelected.set(row.key, row);
    else this.pickerSelected.delete(row.key);
    // Unticking the last row in the "Selected" view (where a failed "Add
    // lines" lands) would leave an empty sheet with the results hidden.
    if (!this.pickerSelected.size && this.pickerView === 'selected') this.pickerView = 'results';
  }

  /** Editing qty / price on a row implies the user wants it. */
  pickerTouched(row: GrnPickerRow) {
    if (!row.selected) this.pickerToggle(row, true);
    if (row.invalid && +row.qty > 0 && +(row.costPrice ?? 0) > 0) row.invalid = false;
  }

  pickerCostChanged(row: GrnPickerRow) {
    this.pickerTouched(row);
    const p = row.costOptions.find((x: any) => +x.costPrice === +(row.costPrice ?? 0));
    if (p) row.mrp = +p.mrp || 0;
  }

  pickerMrpChanged(row: GrnPickerRow) {
    this.pickerTouched(row);
    const p = row.costOptions.find((x: any) => +x.mrp === +row.mrp);
    if (p) row.costPrice = +p.costPrice;
  }

  pickerRemove(row: GrnPickerRow) {
    this.pickerToggle(row, false);
  }

  pickerClearSelection() {
    this.pickerSelected.forEach((r) => (r.selected = false));
    this.pickerSelected.clear();
    this.pickerView = 'results';
  }

  /**
   * Enter in the search box: a scanner sends the code followed by Enter, so
   * a single hit is selected straight away and the box cleared for the next
   * scan. Several hits → move into the sheet.
   */
  pickerSearchEnter() {
    if (this.pickerLoading) return;
    if (this.pickerResults.length === 1) {
      const row = this.pickerResults[0];
      if (!row.selected) this.pickerToggle(row, true);
      else row.qty = (+row.qty || 0) + (row.product?.innerPackaging || 1);
      this.toastr.success(`${row.sku} · ${row.description}`, 'Selected', { timeOut: 1500 });
      this.pickerQuery = '';
      this.pickerRefresh();
      return;
    }
    this.focusPickerRow(0);
  }

  focusPickerRow(i: number) {
    const rows = document.querySelectorAll<HTMLElement>('.grn-picker-sheet tbody tr');
    const el = rows[Math.max(0, Math.min(i, rows.length - 1))];
    el?.focus();
  }

  /** Keyboard on a result row: Space toggles, ↑/↓ move, Enter → qty box. */
  pickerRowKey(e: KeyboardEvent, row: GrnPickerRow, i: number) {
    const inField = (e.target as HTMLElement)?.tagName !== 'TR';
    if (e.key === 'ArrowDown') { e.preventDefault(); this.focusPickerRow(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); i === 0 ? this.pickerInputRef?.nativeElement?.focus() : this.focusPickerRow(i - 1); }
    else if (e.key === ' ' && !inField) { e.preventDefault(); this.pickerToggle(row); }
    else if (e.key === 'Enter' && !inField) {
      e.preventDefault();
      if (!row.selected) this.pickerToggle(row, true);
      const q = document.getElementById('pk-qty-' + row.key) as HTMLInputElement | null;
      q?.focus(); q?.select();
    } else if (e.key === 'Enter' && inField) {
      e.preventDefault();
      this.focusPickerRow(i + 1);
    }
  }

  pickerDialogKey(e: KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); this.closeProductPicker(); }
    else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); this.pickerAddAll(); }
  }

  /** Add every selected row as a received line, then close. */
  pickerAddAll() {
    if (this.pickerAdding || !this.pickerSelected.size) return;
    const rows = Array.from(this.pickerSelected.values());
    rows.forEach((r) => (r.invalid = !(+r.qty > 0) || !(+(r.costPrice ?? 0) > 0)));
    const bad = rows.filter((r) => r.invalid);
    if (bad.length) {
      // Show just the selection with the offending rows marked, and put the
      // cursor in the first one's cost box so it can be keyed straight away.
      this.pickerView = 'selected';
      this.toastr.warning(
        `Enter a cost price above 0 (and a quantity) for ${bad.slice(0, 3).map((r) => r.sku).join(', ')}${bad.length > 3 ? '…' : ''}.`,
        'Cannot add yet',
      );
      setTimeout(() => {
        const first = bad[0];
        const id = (+first.qty > 0 ? 'pk-cp-' : 'pk-qty-') + first.key;
        const el = document.getElementById(id) as HTMLInputElement | null;
        el?.focus();
        el?.select?.();
      }, 50);
      return;
    }

    this.pickerAdding = true;
    let added = 0;
    let merged = 0;
    from(rows)
      .pipe(
        concatMap((row) =>
          this.ensureVariantPricing(row.variant, +(row.costPrice ?? 0), +row.mrp).pipe(
            map((pricingList) => {
              const before = this.grnItems.length;
              this.addOrMergeGrnLine(row.product, row.variant, +row.qty, +(row.costPrice ?? 0), +row.mrp, pricingList, false);
              this.grnItems.length > before ? added++ : merged++;
            }),
            catchError((err) => {
              console.error('Could not add line', row.sku, err);
              this.toastr.error(`${row.sku} could not be added.`, 'Add failed');
              return of(null);
            }),
          ),
        ),
        finalize(() => (this.pickerAdding = false)),
      )
      .subscribe({
        complete: () => {
          this.apportionFreightByWeight();
          this.pickerClearSelection();
          this.closeProductPicker();
          const parts = [];
          if (added) parts.push(`${added} line${added > 1 ? 's' : ''} added`);
          if (merged) parts.push(`${merged} merged into existing line${merged > 1 ? 's' : ''}`);
          if (parts.length) this.toastr.success(parts.join(' · '), 'Lines received');
          setTimeout(() => {
            const el = document.getElementById('qty-' + (this.grnItems.length - 1)) as HTMLInputElement | null;
            el?.scrollIntoView({ block: 'center' });
            el?.focus();
            el?.select();
          }, 100);
        },
      });
  }

  onCostPriceChange(item: any) {
    const entered = +item.costPrice;
    const pricing = item.productVariant?.pricing || [];
    const existing = pricing.find((p: any) => +p.costPrice === entered);
    if (!existing && pricing.length) {
      const last = pricing[0];
      const taxPercent = (+item.gstPercent || 0) + (+item.cessPercent || +item.cessRate || 0);
      const basePrice = +(entered / (1 + taxPercent / 100)).toFixed(4);
      const body: any = { ...last, costPrice: entered, basePrice };
      delete body.id;
      delete body.createdAt;
      delete body.updatedAt;
      body.mrp = Number(body.mrp);
      body.sellingPrice = Number(body.sellingPrice);
      this.productService.addVariantPricing(body).subscribe(() => {
        pricing.unshift(body);
        this.onPriceChange(item, 'costPrice');
      });
    } else {
      this.onPriceChange(item, 'costPrice');
    }
  }

  removeItem(index: number) {
    this.selectedItems.delete(this.grnItems[index]);
    this.grnItems.splice(index, 1);
    // Re-distribute freight across the remaining lines.
    this.apportionFreightByWeight();
  }

  // ── Line grid helpers (design: variance pills, per-row delete, receive all) ──
  get selectedVendorName(): string {
    const id = this.selectedVendorId;
    return (
      (id && this.vendors.find((v) => v.id === id)?.name) ||
      this.selectedPoData?.vendor?.name ||
      this.selectedGinData?.vendorName ||
      ''
    );
  }

  /** short = received below PO qty, excess = above, pending = nothing keyed yet. */
  lineState(item: any): 'short' | 'excess' | 'pending' | 'ok' {
    const po = Number(item?.poQuantity || 0);
    const recd = item?.receivedQuantity;
    if (recd === null || recd === undefined || recd === '') return po > 0 ? 'pending' : 'ok';
    const r = Number(recd || 0);
    if (po > 0 && r < po) return 'short';
    if (po > 0 && r > po) return 'excess';
    return 'ok';
  }

  get visibleGrnItems(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    this.grnItems.forEach((item, index) => {
      if (this.lineFilter !== 'all' && this.lineState(item) !== this.lineFilter) return;
      if (q) {
        const hay = `${item?.skuCode || ''} ${item?.description || ''} ${item?.eanCode || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get shortCount(): number {
    return this.grnItems.filter((i) => this.lineState(i) === 'short').length;
  }

  get excessCount(): number {
    return this.grnItems.filter((i) => this.lineState(i) === 'excess').length;
  }

  get pendingCount(): number {
    return this.grnItems.filter((i) => this.lineState(i) === 'pending').length;
  }

  get grnLineCountLabel(): string {
    const shown = this.visibleGrnItems.length;
    const all = this.grnItems.length;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  get totalPoQty(): number {
    return this.grnItems.reduce((n, i) => n + Number(i?.poQuantity || 0), 0);
  }

  get totalRecdQty(): number {
    return this.grnItems.reduce((n, i) => n + Number(i?.receivedQuantity || 0), 0);
  }

  get totalFreeQty(): number {
    return this.grnItems.reduce((n, i) => n + Number(i?.freeQuantity || 0), 0);
  }

  get totalCostPrice(): number {
    return this.grnItems.reduce((n, i) => n + Number(i?.totalPrice || 0), 0);
  }

  get chargesTotal(): number {
    return (
      Number(this.grnSummary.freightCharges || 0) +
      Number(this.grnSummary.packingCharges || 0) +
      Number(this.grnSummary.otherCharges || 0)
    );
  }

  get discountTotal(): number {
    return (
      Number(this.grnSummary.lineDiscountAmount || 0) +
      Number(this.grnSummary.otherDiscounts || this.otherDiscounts || 0)
    );
  }

  /** Vendor invoice vs what the lines add up to. */
  get invoiceDifference(): number {
    return +(Number(this.invoiceAmount || 0) - Number(this.grnSummary.finalTotal || 0)).toFixed(2);
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
    this.lineQuery = '';
  }

  removeGrnLine(item: any): void {
    if (this.viewOnly) return;
    const idx = this.grnItems.indexOf(item);
    if (idx < 0) return;
    this.removeItem(idx);
    this.updateGrnSummary();
  }

  async clearGrnLines(): Promise<void> {
    if (this.viewOnly || !this.grnItems.length) return;
    const n = this.grnItems.length;
    const proceed = await this.confirmDialog.confirm({
      title: `Clear all ${n} line${n === 1 ? '' : 's'}?`,
      message:
        'Received quantities, free quantities, discounts, batches and expiry dates keyed on these lines will be removed.',
      confirmText: 'Clear lines',
      cancelText: 'Cancel',
      tone: 'neutral',
      icon: 'bi-eraser',
    });
    if (!proceed) return;
    this.grnItems = [];
    this.selectedItems.clear();
    this.clearLineFilters();
    this.updateGrnSummary();
  }

  /** Design: "Receive as ordered" — lines not yet counted take the PO quantity. */
  receiveAsOrdered(): void {
    if (this.viewOnly) return;
    let touched = 0;
    for (const item of this.grnItems) {
      const po = Number(item?.poQuantity || 0);
      if (po <= 0) continue;
      if (this.lineState(item) !== 'pending') continue;
      item.receivedQuantity = po;
      this.onReceivedQtyChange(item);
      touched++;
    }
    this.updateGrnSummary();
    if (touched) this.toastr.info(`${touched} line${touched === 1 ? '' : 's'} set to the ordered quantity.`);
    else this.toastr.info('Every line already has a received quantity.');
  }

  closePricingForm(): void {
    this.isPricingFormVisible = false;
    if (this.editingItem) {
      this.editingItem = null;
      this.selectedProduct = null;
    }
  }

  /** Pricing dialog: apply one of the SKU's known prices to the line being edited. */
  applyKnownPrice(p: any): void {
    if (!this.editingItem || !p) return;
    this.onTableCostPriceChange(this.editingItem, Number(p.costPrice));
    if (p.mrp != null) this.onTableMRPChange(this.editingItem, Number(p.mrp));
    this.updateGrnSummary();
    this.closePricingForm();
  }

  // Multi-select line items for bulk delete
  selectedItems = new Set<any>();

  isItemSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  toggleItemSelection(item: any): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  get selectedCount(): number {
    return this.grnItems.reduce(
      (n: number, it: any) => n + (this.selectedItems.has(it) ? 1 : 0),
      0
    );
  }

  get allItemsSelected(): boolean {
    return this.grnItems.length > 0 && this.selectedCount === this.grnItems.length;
  }

  toggleSelectAll(checked: boolean): void {
    this.selectedItems.clear();
    if (checked) {
      this.grnItems.forEach((it: any) => this.selectedItems.add(it));
    }
  }

  async removeSelectedItems(): Promise<void> {
    const count = this.selectedCount;
    if (!count) return;
    const proceed = await this.confirmDialog.confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${count} selected item${count > 1 ? 's' : ''}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!proceed) return;
    for (let i = this.grnItems.length - 1; i >= 0; i--) {
      if (this.selectedItems.has(this.grnItems[i])) {
        this.grnItems.splice(i, 1);
      }
    }
    this.selectedItems.clear();
    // Re-distribute freight across the remaining lines.
    this.apportionFreightByWeight();
  }
  onRoundOffChanged() {
    // rounding it to 2 decimal places
    this.grnSummary.roundOff = +this.grnSummary.roundOff?.toFixed(2) || 0;

    // Recalculate final total
    this.grnSummary.finalTotal =
      (this.grnSummary.subTotal || 0) +
      (this.grnSummary.gstAmount || 0) +
      (this.grnSummary.cessAmount || 0) +
      (this.grnSummary.freightCharges || 0) +
      (this.grnSummary.packingCharges || 0) +
      (this.grnSummary.otherCharges || 0) -
      (this.grnSummary.schemeDiscountAmount || 0) -
      (this.grnSummary.fixedDiscountAmount || 0) -
      (this.grnSummary.otherDiscounts || 0) -
      (this.grnSummary.overallDiscountAmount || 0) +
      (this.grnSummary.roundOff || 0);
  }

  get totalReceivedQuantity(): number {
    return (
      this.grnItems?.reduce(
        (sum, item) =>
          sum +
          Number(item.receivedQuantity || 0) +
          Number(item.freeQuantity || 0),
        0,
      ) || 0
    );
  }

  get totalProducts(): number {
    return this.grnItems?.length || 0;
  }
  onVendorSelected(vendorId: string) {
    this.selectedVendorId = vendorId;

    this.onVendorChange();
  }

  /**
   * Fetch this vendor's returns awaiting handover (POSTED, goods staged) and
   * those still in preparation. Informational aid, so a failure never blocks
   * GRN entry — the panel shows a muted note instead of raising a toast over
   * the receiving screen.
   */
  private loadOpenPrns(vendorId: string | null): void {
    this.awaitingPrns = [];
    this.awaitingPrnCount = 0;
    this.awaitingPrnValue = 0;
    this.prepPrnCount = 0;
    this.prepPrnValue = 0;
    this.openPrnsError = false;
    this.openPrnsExpanded = false;
    if (!vendorId) {
      this.openPrnsLoading = false;
      return;
    }

    this.openPrnsLoading = true;
    this.prnService.getOpenPrnsByVendor(vendorId).subscribe({
      next: (res) => {
        // A slow response for a vendor the user has already moved off of must
        // not overwrite the current vendor's panel.
        if (this.selectedVendorId !== vendorId) return;
        this.awaitingPrns = res?.awaitingHandover?.prns || [];
        this.awaitingPrnCount = res?.awaitingHandover?.count || 0;
        this.awaitingPrnValue = res?.awaitingHandover?.totalValue || 0;
        this.prepPrnCount = res?.inPreparation?.count || 0;
        this.prepPrnValue = res?.inPreparation?.totalValue || 0;
        this.openPrnsLoading = false;
      },
      error: (err) => {
        if (this.selectedVendorId !== vendorId) return;
        console.error('Failed to load open PRNs for vendor', err);
        this.openPrnsLoading = false;
        this.openPrnsError = true;
      },
    });
  }

  toggleOpenPrns(): void {
    this.openPrnsExpanded = !this.openPrnsExpanded;
  }

  /**
   * POSTED -> DISPATCHED after explicit confirmation. Irreversible (the goods
   * physically leave with the vendor), hence the dialog. On success the row
   * moves out of the awaiting list locally; no full reload needed.
   */
  async markPrnHandedOver(prn: OpenPrn): Promise<void> {
    if (this.dispatching.has(prn.id)) return;

    const proceed = await this.confirmDialog.confirm({
      title: 'Hand over to vendor?',
      message:
        `Confirm that the goods for <strong>${prn.prnNumber}</strong> ` +
        `(${prn.totalQty} unit(s), ${Number(prn.totalAmount).toFixed(2)}) ` +
        `have been physically handed to the vendor's delivery person. ` +
        `This cannot be undone.`,
      confirmText: 'Handed over',
      cancelText: 'Cancel',
      size: 'lg',
    });
    if (!proceed) return;

    this.dispatching.add(prn.id);
    const grnRef = this.grnNumber ? ` (during ${this.grnNumber})` : '';
    this.prnService
      .dispatchPrn(prn.id, `Handed over at GRN receiving${grnRef}`)
      .subscribe({
        next: () => {
          this.dispatching.delete(prn.id);
          this.awaitingPrns = this.awaitingPrns.filter((p) => p.id !== prn.id);
          this.awaitingPrnCount = this.awaitingPrns.length;
          this.awaitingPrnValue = Number(
            this.awaitingPrns
              .reduce((s, p) => s + Number(p.totalAmount || 0), 0)
              .toFixed(2),
          );
          this.toastr.success(`${prn.prnNumber} marked as handed over`);
        },
        error: (err) => {
          this.dispatching.delete(prn.id);
          this.toastr.error(
            err?.error?.message ||
              `Failed to mark ${prn.prnNumber} as handed over`,
          );
        },
      });
  }

  /**
   * Whether to offer the "Open" link on a listed PRN. Mirrors AuthGuard for the
   * `prn/edit/:id` route — RAC decides when permission data is loaded, roles are
   * the legacy fallback — so receiving staff never get sent to /unauthorized.
   */
  get canOpenPrn(): boolean {
    if (this.permissionService.hasPermissionData()) {
      return this.permissionService.canEdit('purchase.prn');
    }
    return this.authService.hasRole(PRN_EDIT_ROLES);
  }

  /** Bootstrap badge class per PRN status. */
  prnStatusClass(status: string): string {
    switch (status) {
      case 'POSTED':
        return 'bg-warning text-dark'; // staged, awaiting handover
      case 'DISPATCHED':
      case 'APPROVED':
        return 'bg-success';
      case 'PENDING':
        return 'bg-info text-dark';
      default:
        return 'bg-secondary';
    }
  }
  onVendorSearch(event: { term?: string; items?: any[] }) {
    const trimmed = event?.term?.trim?.();
    if (trimmed) {
      this.vendorSearch$.next(trimmed);
    }
  }
  onOtherDiscountsChange() {
    this.grnSummary.otherDiscounts = Number(this.otherDiscounts) || 0;
    this.updateGrnSummary();
  }

  saveAsDraft(): void {
    this.isSaved = true;
    this.submitGrn('DRAFT');
  }

  onPoIdChange(selectedId: string) {
    if (!selectedId) {
      this.selectedPoData = null;
      // Back to the header's active store/warehouse.
      this.documentLocationId = null;
      this.selectedVendorId = null;
      this.vendorDisabled = false;
      this.grnItems = [];
      return;
    }
    const found = this.poWithoutGrnOptions.find((po) => po.poId === selectedId);
    if (found) {
      this.selectedPoNumber = found.poNumber;

      // 🚀 Proactively check for vendor in 'found' while API loads
      const preVendorObj =
        found.vendor || found.purchaseOrder?.vendor || found.vendorData;
      const preVendorId =
        found.vendorId || preVendorObj?.id || found.purchaseOrder?.vendorId;

      if (preVendorObj && typeof preVendorObj === 'object') {
        if (!this.vendors.some((v) => v.id === preVendorObj.id)) {
          this.vendors = [...this.vendors, preVendorObj];
        }
      }

      if (preVendorId) {
        this.selectedVendorId = String(preVendorId);
        this.vendorDisabled = true;
        this.cdr.detectChanges();
      }

      this.poService.getPendingItems(found.poId).subscribe({
        next: (po: any) => {
          this.onPoSelected(po); // This will handle items and re-verifying vendor
        },
        error: (err) => console.error('Error fetching PO by ID', err),
      });
    }
  }

  onGinIdChange(selectedId: string) {
    if (!selectedId) {
      this.selectedGinData = null;
      this.selectedVendorId = null;
      this.vendorDisabled = false;
      this.grnItems = [];
      return;
    }
    const found = this.ginWithoutGrnOptions.find(
      (gin) => gin.ginId === selectedId,
    );
    if (found) {
      this.grnService.getGinById(found.ginId).subscribe({
        next: (ginData: any) => {
          this.onGinSelected(ginData);
        },
        error: (err) => console.error('Error fetching GIN by ID', err),
      });
    }
  }

  onGinSelected(ginData: any) {
    if (ginData) {
      this.selectedGinData = ginData;

      if (ginData.vendorId || ginData.vendor) {
        this.selectedVendorId = String(ginData.vendorId || ginData.vendor?.id);
        this.vendorDisabled = true;
      }

      const poId = ginData.purchaseOrderId || ginData.purchaseOrder?.id || ginData.poId;
      let poNum = ginData.poNumber || ginData.purchaseOrder?.poNumber || ginData.po?.poNumber || '';

      if (poId) {
        // Fallback trace for PO retrieval
        console.log('[onGinSelected] Attempting to resolve PO:', { poId, poNum });

        const setPoDetails = (id: string, num: string, vendor: any = null) => {
          this.selectedPoData = { id, poNumber: num, vendor };
          this.selectedPoNumber = num;

          if (num && id) {
            const isPoInOptions = this.poOptions.some(po => po.id === id);
            if (!isPoInOptions) {
              this.poOptions = [...this.poOptions, { poNumber: num, id }];
            }
          }
          this.cdr.detectChanges();
          console.log('[onGinSelected] PO Resolved:', { id, poNumber: num });
        };

        if (poNum) {
          setPoDetails(poId, poNum, ginData.vendor || ginData.purchaseOrder?.vendor || null);
        } else {
          // Attempt local lookup first
          const matchedPo = this.poOptions.find(p => p.id === poId) ||
            this.poWithoutGrnOptions.find(p => p.poId === poId || p.id === poId);

          if (matchedPo) {
            poNum = matchedPo.poNumber || matchedPo.purchaseOrder?.poNumber || '';
            setPoDetails(poId, poNum, matchedPo.vendor || matchedPo.purchaseOrder?.vendor || null);
          } else {
            // 🚀 Final Fallback: Fetch from API as requested
            console.log(`[onGinSelected] Fetching PO details from API for ID: ${poId}`);
            this.poService.getPoById(poId).subscribe({
              next: (poData: any) => {
                const resolvedPoNum = poData.poNumber || poData.purchaseOrder?.poNumber || '';
                setPoDetails(poId, resolvedPoNum, poData.vendor || poData.purchaseOrder?.vendor || null);
              },
              error: (err) => console.error('[onGinSelected] Error fetching PO by ID', err)
            });
          }
        }
      }

      this.cdr.detectChanges();

      setTimeout(() => {
        this.invoiceNumber = ginData.invoiceNumber || '';
        this.invoiceDate = ginData.invoiceDate
          ? ginData.invoiceDate.split('T')[0]
          : '';
        console.log(
          'Updated Invoice Data:',
          this.invoiceNumber,
          this.invoiceDate,
        );
      });

      // 1. Identify unique GST Rate IDs
      const uniqueGstIds = new Set<string>();
      ginData.items.forEach((item: any) => {
        const product = item.productVariant?.product;
        if (product?.purchaseGstRateId) {
          uniqueGstIds.add(product.purchaseGstRateId);
        }
      });

      // 2. Create Observables for each GST Rate ID
      const gstRequests: any = {};
      uniqueGstIds.forEach((id) => {
        gstRequests[id] = this.gstSlabService.getGstSlabById(id).pipe(
          catchError(() => of(null)), // Handle errors gracefully
        );
      });

      // 3. Fetch all GST Rates
      forkJoin(gstRequests).subscribe((gstRatesMap: any) => {
        // 4. Map Items with GST Rates
        this.grnItems = ginData.items.map((item: any) => {
          const variant = item.productVariant;
          const product = variant?.product || {};
          const pricing = item.variantPricing || {};

          // Resolve GST Percent
          let gstPercent = 0;
          if (
            product?.purchaseGstRateId &&
            gstRatesMap[product.purchaseGstRateId]
          ) {
            gstPercent = +gstRatesMap[product.purchaseGstRateId].value || 0;
          }

          const costPrice = +pricing.costPrice || 0;
          const cessRate = +product.cessRate || 0;
          const basicCostPrice = consistentBasicCost(costPrice, gstPercent + cessRate, pricing.basePrice);
          const totalQty = item.totalQty || 0;
          const breakup = this.computeLineTaxBreakup(
            basicCostPrice, costPrice, totalQty, gstPercent, cessRate,
          );
          const gstAmount = breakup.gstAmount;

          return {
            productVariant: variant,
            productVariantId: item.productVariantId,
            skuCode: item.skuCode || product?.sku || '',
            pluCode: pricing.pluCode || '',
            description: product?.longDescription || '',
            eanCode: currentEanOf(variant),
            innerQuantity: product?.innerPackaging || 1,
            uom: product?.uom || '',
            poQuantity: totalQty,
            // Decide whether this line's quantity box accepts decimals: a kg/gm/ltr
            // unit, or a bulk / weighable / bulk-child product.
            isBulk: product?.isBulk ?? null,
            isWeighable: product?.isWeighable ?? null,
            bulkParentProductId: product?.bulkParentProductId ?? null,
            receivedQuantity: totalQty,
            freeQuantity: item.freeQuantity || 0,
            actualCostPrice: basicCostPrice,
            basicCostPrice,
            gstPercent: gstPercent,
            gstAmount: gstAmount,
            costPrice: costPrice,
            cessRate: cessRate,
            cessAmount: breakup.cessAmount,
            mrp: Number(pricing.mrp || 0),
            sellingPrice: Number(item.sellingPrice || pricing.sellingPrice || 0),
            freightCharges: 0,
            packingCharges: 0,
            totalPrice: breakup.lineGross,
            discountOnePercent: 0,
            discountOneAmount: 0,
            discountTwoPercent: 0,
            discountTwoAmount: 0,
            cashDiscountPercent: 0,
            cashDiscountAmount: 0,
            schemeDiscountPercent: 0,
            schemeDiscountAmount: 0,
            fixedDiscountPercent: 0,
            fixedDiscountAmount: 0,
            overAllDiscountAmount: 0,
            basicCostTotal: breakup.basicCostTotal,
            totalPriceWithoutGST: breakup.basicCostTotal,
            oldRoiPercent: 0, // Calculate if needed
            newRoiPercent: 0,
            hsnCode: product?.hsnCode || '',
            id: item?.id,
            batchNumber: '',
            expiryDate: null,
          };
        });

        // Update summary after mapping
        this.updateGrnSummary();
      });
    }
  }

  setGinOption(ginId: string, ginNumber: string) {
    this.selectedGinData = {
      id: ginId,
      ginNumber: ginNumber,
      vendorId: this.selectedVendorId,
    };

    // Ensure it's in the options list so it displays correctly
    const isGinPresent = this.ginOptions.some((g) => g.id === ginId);
    if (!isGinPresent) {
      this.ginOptions = [
        ...this.ginOptions,
        {
          id: ginId,
          ginNumber: ginNumber,
        },
      ];
    }
  }

  onSearchResultSelected(product: any) {
    if (!product) return;

    this.selectedProduct = product;
    console.log(`[GRN Search Select] Product: ${product.longDescription}, Margin: ${product.marginPercentage || product.margin_percentage}%, GST: ${product.purchaseGstRate?.value || product.gstPercent}%`);
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.selectedMRP = +this.selectedVariant?.pricing?.[0]?.mrp || 0;
    this.warnIfUnpriced(product, this.selectedVariant);

    // Focus logic after selection
    setTimeout(() => {
      // Check if product already exists in the GRN table
      const existingItemIndex = this.grnItems.findIndex(item => item.skuCode === product.sku);

      if (existingItemIndex !== -1) {
        // If it exists, focus the quantity field in the table
        const element = document.getElementById('qty-' + existingItemIndex);
        if (element) {
          element.focus();
          (element as HTMLInputElement).select(); // Optional: select text for easy overwrite
        }
      } else {
        // If it's a new selection, focus the manual quantity input
        this.quantityInputRef?.nativeElement?.focus();
        this.quantityInputRef?.nativeElement?.select();
      }
    }, 100);

    // Show success message
    this.toastr.success(
      `Product found: ${product.longDescription} `,
      'Product Selected',
    );

    // Keep the search/preloaded list intact so the user can keep adding more
    // products from the same vendor. Typing in the SKU box refreshes it via
    // the typeahead; wiping it here left the dropdown empty after one add.
  }

  // ✅ NEW: Handle search type change (SKU/PLU/EAN/General)
  onSearchTypeChange() {
    this.searchResults = [];
    this.eanSearchResults = [];
    // Clear current selection when search type changes
    if (this.selectedProduct) {
      this.selectedProduct = null;
    }
  }

  onCancel(): void {
    // Navigate back to the GRN list page
    this.router.navigate(['/grn']);
  }

  // Invoice amount validation helpers
  isInvoiceAmountValid(): boolean {
    if (this.invoiceAmount <= 0) return false;
    const tolerance = environment.invoiceAmountTolerance ?? 0;
    return (
      Math.abs(this.invoiceAmount - this.grnSummary.finalTotal) <= tolerance
    );
  }

  get invoiceAmountError(): string {
    if (!this.invoiceAmount) {
      return 'Invoice amount is required';
    }
    const tolerance = environment.invoiceAmountTolerance ?? 0;
    if (
      Math.abs(this.invoiceAmount - this.grnSummary.finalTotal) > tolerance
    ) {
      return `Invoice amount(₹${this.invoiceAmount}) does not match Grand Total(₹${this.grnSummary.finalTotal}) (tolerance ±₹${tolerance})`;
    }
    return '';
  }

  async canDeactivate(): Promise<boolean> {
    if (this.viewOnly) {
      return true;
    }
    if (this.isSaved) {
      return true;
    }
    if (
      this.grnItems.length > 0 ||
      this.invoiceNumber ||
      this.selectedPoNumber ||
      this.selectedVendorId
    ) {
      const leave = await this.confirmDialog.confirm({
        title: 'Discard changes?',
        message:
          'You have unsaved changes in <strong>Goods Receipt Notes</strong>. Are you sure you want to leave this page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
      // Leaving on purpose discards the browser copy too. Not when this is
      // the sign-out path (no token by now): that copy is the user's way back.
      if (leave && this.authService.isLoggedIn()) this.discardDraft();
      return leave;
    }
    return true;
  }

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // A long GRN lived only in memory until Save Draft, so an expired session
  // or a closed tab lost it. The form now copies itself to the browser every
  // few seconds (FormDraftStore) and offers the copy back when reopened.
  private draftStore: FormDraftStore<GrnDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank form
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Saved to the server, or discarded on purpose: stop copying. */
  private draftClosed = false;

  private initDraft(grnId: string | null): void {
    if (this.viewOnly) return;
    this.draftStore = new FormDraftStore<GrnDraftSnapshot>([
      'grn',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      grnId ?? 'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private hasDraftContent(d: Partial<GrnDraftSnapshot> | null | undefined): boolean {
    return !!(d?.grnItems?.length || d?.invoiceNumber || d?.selectedVendorId);
  }

  private draftSnapshot(): GrnDraftSnapshot {
    return {
      selectedVendorId: this.selectedVendorId,
      vendor: this.vendors.find((v: any) => v.id === this.selectedVendorId) ?? null,
      vendorDisabled: this.vendorDisabled,
      selectedPoNumber: this.selectedPoNumber,
      selectedPoData: this.selectedPoData,
      selectedGinData: this.selectedGinData,
      documentLocationId: this.documentLocationId,
      invoiceNumber: this.invoiceNumber,
      invoiceDate: this.invoiceDate,
      receivedDate: this.receivedDate,
      invoiceAmount: this.invoiceAmount,
      freightCharges: this.freightCharges,
      packingCharges: this.packingCharges,
      otherDiscounts: this.otherDiscounts,
      overallDiscountAmount: this.overallDiscountAmount,
      costPriceIncludesGst: this.costPriceIncludesGst,
      editReasonId: this.editReasonId,
      editReasonNote: this.editReasonNote,
      grnItems: this.grnItems,
    };
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.draftSnapshot();
    // An emptied form leaves any earlier copy alone ("Not now" kept it).
    if (this.hasDraftContent(snapshot)) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /**
   * Offer a copy left by an earlier visit. For an existing GRN a copy older
   * than the version on the server is stale and silently dropped.
   */
  private async offerDraftRestore(serverUpdatedAt?: string): Promise<void> {
    const stored = this.draftStore?.load();
    if (!stored || !this.hasDraftContent(stored.data)) {
      this.draftReady = true;
      return;
    }
    if (serverUpdatedAt && stored.savedAt <= Date.parse(serverUpdatedAt)) {
      this.draftStore!.clear();
      this.draftReady = true;
      return;
    }

    const d = stored.data;
    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const lines = d.grnItems?.length ?? 0;
    const details = [
      d.vendor?.name ? `vendor <strong>${escapeHtml(d.vendor.name)}</strong>` : '',
      d.invoiceNumber ? `invoice <strong>${escapeHtml(d.invoiceNumber)}</strong>` : '',
      `<strong>${lines}</strong> line${lines === 1 ? '' : 's'}`,
    ].filter(Boolean);
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved GRN?',
      message:
        `This GRN was not saved. A copy from ${when} is still on this computer: ${details.join(', ')}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you start entering a GRN here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(d);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: GrnDraftSnapshot): void {
    // Make sure the pickers can show the restored values.
    if (d.vendor?.id && !this.vendors.some((v: any) => v.id === d.vendor.id)) {
      this.vendors = [...this.vendors, d.vendor];
    }
    const poId = d.selectedPoData?.id;
    if (poId && !this.poOptions.some((po: any) => po.id === poId)) {
      this.poOptions = [
        ...this.poOptions,
        {
          id: poId,
          poNumber: d.selectedPoData.poNumber || d.selectedPoNumber,
          vendorName: d.vendor?.name ?? null,
        },
      ];
    }
    if (d.selectedGinData?.id && d.selectedGinData?.ginNumber) {
      this.setGinOption(d.selectedGinData.id, d.selectedGinData.ginNumber);
    }

    this.selectedPoData = d.selectedPoData;
    this.selectedPoNumber = d.selectedPoNumber;
    this.selectedGinData = d.selectedGinData;
    this.documentLocationId = d.documentLocationId;
    this.vendorDisabled = d.vendorDisabled;
    this.selectedVendorId = d.selectedVendorId;
    this.invoiceNumber = d.invoiceNumber;
    this.invoiceDate = d.invoiceDate;
    this.receivedDate = d.receivedDate;
    this.invoiceAmount = d.invoiceAmount;
    this.freightCharges = d.freightCharges;
    this.packingCharges = d.packingCharges;
    this.otherDiscounts = d.otherDiscounts;
    this.overallDiscountAmount = d.overallDiscountAmount;
    this.costPriceIncludesGst = d.costPriceIncludesGst;
    this.editReasonId = d.editReasonId;
    this.editReasonNote = d.editReasonNote;
    this.grnItems = d.grnItems ?? [];

    this.lastValidInvoiceDate = this.invoiceDate;
    this.lastValidReceivedDate = this.receivedDate;
    this.syncReceivedDateForConfig();
    this.grnSummary.otherDiscounts = Number(this.otherDiscounts) || 0;
    this.updateGrnSummary();
    this.cdr.detectChanges();
    this.toastr.success(
      `${this.grnItems.length} line(s) restored. Save Draft to keep them on the server.`,
      'GRN restored',
    );
  }

  // Add these methods to your CreateGrnComponent class

  // Method to handle cost price selection and auto-select corresponding MRP
  onCostPriceSelected(selectedCostPrice: number) {
    if (!selectedCostPrice || !this.selectedProduct) return;

    this.enteredCostPrice = selectedCostPrice;

    // Find the corresponding MRP for the selected cost price
    const correspondingPricing = this.findPricingByCostPrice(selectedCostPrice);
    if (correspondingPricing) {
      this.selectedMRP = correspondingPricing.mrp;
    }
  }

  // Method to handle MRP selection and auto-select corresponding cost price
  onMRPSelected(selectedMRP: number) {
    if (!selectedMRP || !this.selectedProduct) return;

    this.selectedMRP = selectedMRP;

    // Find the corresponding cost price for the selected MRP
    const correspondingPricing = this.findPricingByMRP(selectedMRP);
    if (correspondingPricing) {
      this.enteredCostPrice = correspondingPricing.costPrice;
    }
  }

  // Helper method to find pricing by cost price
  private findPricingByCostPrice(costPrice: number): any | null {
    if (!this.selectedProduct?.variants) return null;

    for (const variant of this.selectedProduct.variants) {
      if (variant.pricing) {
        const pricing = variant.pricing.find(
          (p: any) => +p.costPrice === +costPrice,
        );
        if (pricing) return pricing;
      }
    }
    return null;
  }

  // Helper method to find pricing by MRP
  private findPricingByMRP(mrp: number): any | null {
    if (!this.selectedProduct?.variants) return null;

    for (const variant of this.selectedProduct.variants) {
      if (variant.pricing) {
        const pricing = variant.pricing.find((p: any) => +p.mrp === +mrp);
        if (pricing) return pricing;
      }
    }
    return null;
  }

  // Method to handle cost price changes in the table
  onTableCostPriceChange(item: any, newCostPrice: number) {
    if (!newCostPrice || !item.productVariant) return;

    item.costPrice = newCostPrice;

    // Find corresponding MRP for the new cost price
    const correspondingPricing = item.productVariant.pricing?.find(
      (p: any) => +p.costPrice === +newCostPrice,
    );

    if (correspondingPricing) {
      item.mrp = correspondingPricing.mrp;
      item.sellingPrice = correspondingPricing.sellingPrice;
      item.actualCostPrice = correspondingPricing.basePrice;

      // ✅ SANITIZE after selection change
      this.sanitizeItemPricing(item);
    }

    // Trigger price change calculations
    this.onCostPriceChange(item);
  }

  // Method to handle MRP changes in the table
  onTableMRPChange(item: any, newMRP: number) {
    if (!newMRP || !item.productVariant) return;

    item.mrp = newMRP;

    // Find the price row for the new MRP. Several rows can share an MRP
    // (e.g. a stray low-cost row): keep the one matching the current cost
    // price, and only otherwise take the first row with that MRP.
    const sameMrp = (item.productVariant.pricing || []).filter(
      (p: any) => +p.mrp === +newMRP,
    );
    const correspondingPricing =
      sameMrp.find((p: any) => +p.costPrice === +item.costPrice) || sameMrp[0];

    if (correspondingPricing) {
      item.costPrice = correspondingPricing.costPrice;
      item.sellingPrice = correspondingPricing.sellingPrice;
      // Follow the paired basic price stored on this pricing row — don't derive.
      const matchedBasic = +correspondingPricing.basePrice || 0;
      if (matchedBasic > 0) {
        item.actualCostPrice = matchedBasic;
        item.basicCostPrice = matchedBasic;
      }

      // ✅ SANITIZE after selection change
      this.sanitizeItemPricing(item);

      // Trigger price change calculations — onPriceChange now resolves the
      // stored basic cost itself and runs the toggle-aware breakup.
      this.onPriceChange(item, 'costPrice');
    }
  }

  // Updated populatePriceOptions method to create price-MRP pairs
  populatePriceOptions(products: any) {
    this.allCostPrices = [];
    this.allMRPs = [];

    if (products?.variants && products.variants.length) {
      // Create a Set to avoid duplicates
      const costPriceSet = new Set<number>();
      const mrpSet = new Set<number>();

      products.variants.forEach((variant: any) => {
        if (variant.pricing && variant.pricing.length) {
          variant.pricing.forEach((price: any) => {
            const costPriceNum = price.costPrice ? +price.costPrice : 0;
            const mrpNum = price.mrp ? +price.mrp : 0;

            if (costPriceNum > 0) {
              costPriceSet.add(costPriceNum);
            }
            if (mrpNum > 0) {
              mrpSet.add(mrpNum);
            }
          });
        }
      });

      // Keep the API order: most recently updated price first.
      this.allCostPrices = Array.from(costPriceSet);
      this.allMRPs = Array.from(mrpSet);
    }
  }

  onInvoiceDateChange(event: Event) {
    const newDate = (event.target as HTMLInputElement).value;

    // Attempt to validate the new date
    const isValid = this.validateInvoiceDate(newDate);

    if (isValid) {
      // If valid, update the component property and store it as the last valid date
      this.invoiceDate = newDate;
      this.lastValidInvoiceDate = newDate;
    } else {
      // If invalid (future date), display the toast error (done in validateInvoiceDate)

      // Block the invalid value from being set by resetting the input element's value
      // We use a setTimeout to ensure the DOM update happens after the Angular change detection cycle.
      setTimeout(() => {
        this.invoiceDate = this.lastValidInvoiceDate;
      }, 0);
    }
  }

  /**
   * Reads Configuration > "GRN backdating limit (days)" and derives the `min`
   * shared by the invoice-date and received-date pickers. A null limit (unset,
   * or the call failed) leaves them unrestricted — the server is the real guard
   * on submit.
   */
  private loadBackdateLimit(): void {
    this.configurationService
      .getValue(SETTING_KEYS.GRN_BACKDATE_LIMIT_DAYS)
      .subscribe((value) => {
        this.backdateLimitDays = typeof value === 'number' ? value : null;
        this.minGrnDate = this.earliestAllowedGrnDate();
      });
  }

  private loadAllowReceivedDateEntry(): void {
    this.configurationService
      .getValue(SETTING_KEYS.GRN_ALLOW_RECEIVED_DATE_ENTRY)
      .subscribe((value) => {
        this.allowReceivedDateEntry = value === true;
        this.syncReceivedDateForConfig();
      });
  }

  /**
   * With entry off, an unposted GRN displays today rather than the draft-save
   * placeholder the DB holds, since today is what the server will stamp on
   * submit. Called from both the setting load and the GRN load — either can
   * finish first. A POSTED GRN keeps its stored date.
   */
  private syncReceivedDateForConfig(): void {
    if (!this.allowReceivedDateEntry && this.grnStatus !== 'POSTED') {
      this.receivedDate = this.todayDate;
      this.lastValidReceivedDate = this.todayDate;
    }
  }

  /**
   * Reads Configuration > "Allow editing posted GRNs". The list page hides the
   * Edit action when it is off, but the route is still reachable by URL, so the
   * form locks itself too rather than letting the user retype a posted receipt
   * only for the server to reject the save.
   */
  private loadAllowEditPosted(): void {
    this.configurationService
      .getValue(SETTING_KEYS.GRN_ALLOW_EDIT_POSTED)
      .subscribe((value) => {
        this.allowEditPosted = value === true;
        this.applyPostedEditLock();
      });
  }

  /**
   * Force read-only when a POSTED GRN is opened and posted edits are off.
   * Called from both the setting load and the GRN load — either can win that
   * race, and the lock must apply whichever arrives second. A null setting
   * (still loading, or the call failed) never unlocks anything on its own.
   */
  private applyPostedEditLock(): void {
    if (this.grnStatus === 'POSTED' && this.allowEditPosted === false) {
      this.viewOnly = true;
    }
  }

  /**
   * Reason Master entries of type GRN, offered when amending a posted receipt.
   * A failure leaves the list empty and the panel says so — the server rejects
   * an amendment with no reason regardless, so failing quiet here would only
   * move the error later.
   */
  private loadAmendmentReasons(): void {
    this.reasonsService.getAllReasons().subscribe({
      next: (list) => {
        this.amendmentReasons = (list || []).filter(
          (r) => r.type === 'GRN' && r.isActive !== false,
        );
      },
      error: () => (this.amendmentReasons = []),
    });
  }

  openRevisionHistory(): void {
    if (!this.grnId) return;
    this.revisionHistoryVisible = true;
    this.revisionsLoading = true;
    this.revisionsError = false;
    this.grnService.getGrnRevisions(this.grnId).subscribe({
      next: (res) => {
        this.revisions = res?.revisions || [];
        this.revisionsLoading = false;
      },
      error: () => {
        this.revisionsError = true;
        this.revisionsLoading = false;
      },
    });
  }

  /**
   * Header fields that differ between the two snapshots of one revision, for
   * the history table. Line-level detail is deliberately summarised rather
   * than exploded here: the full before/after lines are in the snapshots, and
   * the stock table below already shows what physically moved.
   */
  revisionFieldDiff(
    rev: GrnRevision,
  ): Array<{ label: string; before: string; after: string }> {
    const before = rev?.beforeSnapshot || {};
    const after = rev?.afterSnapshot || {};
    const fields: Array<[string, string]> = [
      ['invoiceNumber', 'Invoice no'],
      ['invoiceDate', 'Invoice date'],
      ['receivedDate', 'Received date'],
      ['totalQty', 'Total qty'],
      ['totalItems', 'Total items'],
      ['gstAmount', 'GST amount'],
      ['totalAmount', 'Total amount'],
    ];
    const show = (v: any) =>
      v === null || v === undefined || v === '' ? '-' : String(v);

    return fields
      .filter(([key]) => show(before[key]) !== show(after[key]))
      .map(([key, label]) => ({
        label,
        before: show(before[key]),
        after: show(after[key]),
      }));
  }

  /** SKU for a variant in a revision's stock table, read off its snapshots. */
  skuForVariant(rev: GrnRevision, variantId: string): string {
    const lines = [
      ...(rev?.afterSnapshot?.items || []),
      ...(rev?.beforeSnapshot?.items || []),
    ];
    const hit = lines.find((l: any) => l?.productVariantId === variantId);
    return hit?.skuCode || variantId;
  }

  /** yyyy-MM-dd of the oldest selectable GRN date; '' when unlimited. */
  private earliestAllowedGrnDate(): string {
    if (this.backdateLimitDays === null) return '';
    // Step back in UTC so the arithmetic is offset- and DST-free; todayDate is
    // already the IST calendar day, and UTC midnight is just its container.
    const today = new Date(`${this.todayDate}T00:00:00Z`);
    const earliest = new Date(
      today.getTime() - this.backdateLimitDays * 24 * 60 * 60 * 1000,
    );
    return earliest.toISOString().slice(0, 10);
  }

  /**
   * Shared future-date + backdating-window check for the GRN date fields.
   * `min`/`max` on the inputs block the picker, but typed dates still land
   * here, so this is the client-side guard.
   *
   * Compares yyyy-MM-dd strings directly: they sort chronologically, so this
   * needs no Date parsing and cannot be shifted by the browser's timezone.
   */
  private validateGrnDate(dateString: string, fieldLabel: string): boolean {
    if (!dateString) return true;

    const inputDay = dateString.slice(0, 10);

    if (inputDay > this.todayDate) {
      this.toastr.error(
        `${fieldLabel} cannot be a future date. The date has been reverted.`,
        'Validation Error',
      );
      return false;
    }

    if (this.backdateLimitDays !== null) {
      const earliest = this.earliestAllowedGrnDate();

      if (inputDay < earliest) {
        this.toastr.error(
          this.backdateLimitDays === 0
            ? `${fieldLabel} must be today. Backdating is not allowed. The date has been reverted.`
            : `${fieldLabel} cannot be backdated more than ${this.backdateLimitDays} day(s). The earliest allowed date is ${earliest}. The date has been reverted.`,
          'Validation Error',
        );
        return false;
      }
    }

    return true;
  }

  // Validation logic helper (Now accepts the new date string)
  validateInvoiceDate(dateString: string): boolean {
    return this.validateGrnDate(dateString, 'Invoice Date');
  }

  /** Mirrors onInvoiceDateChange: reverts the field when the date is invalid. */
  onReceivedDateChange(event: Event) {
    const newDate = (event.target as HTMLInputElement).value;

    if (this.validateGrnDate(newDate, 'Received Date')) {
      this.receivedDate = newDate;
      this.lastValidReceivedDate = newDate;
    } else {
      setTimeout(() => {
        this.receivedDate = this.lastValidReceivedDate;
      }, 0);
    }
  }

  // Method to open the new pricing form/modal\
  // create-grn.component.ts

  // ... (inside CreateGrnComponent class)

  /**
   * Opens the New Pricing modal, populating it with data from either
   * a selected product (for manual add) or a specific GRN item (from the table).
   * @param item Optional: The GRN line item object.
   */
  // openNewPricingForm(item?: any): void {
  //   console.log(`Opening new pricing form for item: ${ JSON.stringify(item) } `);
  //   let product: any = null;
  //   let costPrice = 0;
  //   let mrp = 0;
  //   let sellingPrice = 0;
  //   let basePrice = 0;

  //   if (item) {
  //     console.log(`if block item: ${ JSON.stringify(item) } `);
  //     // Case 1: Item clicked from the GRN table
  //     product = item;
  //     costPrice = +item.costPrice || 0;
  //     mrp = +item.mrp || 0;
  //     sellingPrice = +item.sellingPrice || 0;
  //     basePrice = +item.actualCostPrice || 0;

  //     // IMPORTANT: Set selectedProduct/selectedVariant temporarily
  //     // for other pricing methods (like saveNewPricing) to work correctly
  //     this.selectedProduct = product || item;
  //     console.log(
  //       `this.selectedProduct: ${ JSON.stringify(this.selectedProduct) } `
  //     );
  //     this.selectedVariant = item.productVariant;
  //   } else if (this.selectedProduct && this.selectedVariant) {
  //     // Case 2: Clicked from the manual product add section
  //     product = this.selectedProduct || item;
  //     costPrice = this.enteredCostPrice || 0;
  //     mrp = this.selectedMRP || 0;
  //     sellingPrice = this.selectedVariant.pricing?.[0]?.sellingPrice || 0;
  //     basePrice = this.enteredCostPrice || 0; // Assuming entered CostPrice is the new base/cost
  //   }
  //   console.log(`product: ${ JSON.stringify(product) } `);

  //   if (!product) {
  //     this.toastr.warning('Please select a product first.', 'Warning');
  //     return;
  //   }

  //   // Pre-populate with determined values
  //   this.newPricing = {
  //     basePrice: basePrice,
  //     costPrice: costPrice,
  //     sellingPrice: sellingPrice,
  //     mrp: mrp,
  //   };

  //   this.isPricingFormVisible = true;
  //   console.log(`isPricingFormVisible: ${ this.isPricingFormVisible } `);
  // }

  openNewPricingForm(item?: any): void {
    console.log('[GRN Open Modal] Item clicked:', item);
    this.editingItem = item || null;
    // ... (existing variable declarations) ...

    let product: any = null;
    // Initialize all values to ZERO to start fresh, regardless of the item being passed.
    let costPrice = 0;
    let mrp = 0;
    let sellingPrice = 0;
    let basePrice = 0;

    // 1. Determine the product context
    if (item) {
      // Case 1: Item clicked from the GRN table (we still start the form empty but link the product)
      product = item;
      this.selectedProduct = product;
      this.selectedVariant = item.productVariant;
    } else if (this.selectedProduct && this.selectedVariant) {
      // Case 2: Clicked from the manual product add section
      product = this.selectedProduct;
    }

    if (!product) {
      this.toastr.warning('Please select a product first.', 'Warning');
      return;
    }

    // 2. Clear or populate newPricing before opening
    this.newPricingValidationError = null; // Clear previous errors
    // Capture the existing EAN so it can be shown as a placeholder. The input
    // itself starts blank: leaving it blank keeps the existing EAN, while
    // entering a value updates it.
    this.currentEanCode =
      (item?.eanCode || currentEanOf(this.selectedVariant) || '') + '';
    // Every barcode the SKU carries (identity table, primary first, then
    // pricing-history codes) — a code tagged on the Bulk EAN grid without
    // being made primary must still be visible here.
    this.variantBarcodes = allCodesOf(this.selectedVariant);
    this.variantPrimaryEan = currentEanOf(this.selectedVariant);
    this.newPricing = {
      basePrice: item ? Number(item.actualCostPrice || item.basePrice || 0) : 0,
      costPrice: item ? Number(item.costPrice || 0) : 0,
      sellingPrice: item ? Number(item.sellingPrice || 0) : 0,
      mrp: item ? Number(item.mrp || 0) : 0,
      eanCode: '',
    };
    this.sellingAtMrpAsked = false;
    this.sellingFollowsMrp = false;
    // Margin-driven CP starts OFF every time; the % is pre-filled from the
    // product master so enabling it needs no lookup.
    this.useMarginForCp = false;
    this.pricingMarginPercent = this.getProductMarginPercent();

    // 3. Open the modal
    this.isPricingFormVisible = true;
  }

  private getProductMarginPercent(): number {
    const p: any = this.selectedProduct;
    return Number(
      p?.marginPercentage ??
      p?.margin_percentage ??
      p?.product?.marginPercentage ??
      p?.product?.margin_percentage ??
      p?.productVariant?.product?.marginPercentage ??
      p?.productVariant?.product?.margin_percentage ??
      0
    ) || 0;
  }

  onMarginSettingChange(): void {
    if (this.useMarginForCp && (Number(this.newPricing.mrp) || 0) > 0) {
      this.calculateNewPricing('mrp');
    } else {
      this.validatePricing();
    }
  }

  // Method to save the new pricing tier
  saveNewPricing(): void {
    if (!this.selectedVariant || !this.selectedProduct) return;
    if (!this.validatePricing()) {
      // Validation failed, toastr error shown in validatePricing()
      return;
    }

    // Send the EAN only when the operator entered a new value. A blank field
    // keeps the existing EAN untouched (handled on the backend).
    const enteredEan = (this.newPricing.eanCode || '').trim();

    const body: any = {
      variantId: this.selectedVariant.id, // Mandatory ID of the variant
      mrp: Number(this.newPricing.mrp),
      costPrice: Number(this.newPricing.costPrice),
      basePrice: Number(this.newPricing.basePrice),
      sellingPrice: Number(this.newPricing.sellingPrice),
      validFrom: new Date().toISOString(),
    };
    if (enteredEan) {
      body.eanCode = enteredEan;
    }

    this.productService.addVariantPricing(body).subscribe({
      next: (res: any) => {
        this.toastr.success('New pricing added successfully!', 'Success');

        // 1. Update the local variant pricing list so the new price can be
        // picked from the dropdowns immediately. A line loaded without a
        // pricing array (GIN lines used to arrive that way) must not throw
        // here — that left the popup open after the row had been created.
        if (!Array.isArray(this.selectedVariant.pricing)) {
          this.selectedVariant.pricing = [];
        }
        this.selectedVariant.pricing.unshift(res);

        // A new EAN is already on the pricing row just unshifted, so
        // currentEanOf(this.selectedVariant) reflects it without a reload.

        // 2. Select the newly added price in the GRN form
        // 3. Update the specific item in the table if it was passed
        if (this.editingItem) {
          this.editingItem.costPrice = res.costPrice;
          this.editingItem.mrp = res.mrp;
          this.editingItem.sellingPrice = res.sellingPrice;
          if (enteredEan) {
            this.editingItem.eanCode = enteredEan;
          }

          this.onTableCostPriceChange(this.editingItem, res.costPrice);
        }

        // 4. Close the form
        this.isPricingFormVisible = false;
        this.editingItem = null;
      },
      error: (err) => {
        this.toastr.error('Failed to add new pricing.', 'Error');
        console.error('Error adding pricing:', err);
      },
    });
  }

  // Add this method to your CreateGrnComponent
  // validatePricing(): boolean {
  //   const { basePrice, costPrice, sellingPrice, mrp } = this.newPricing;

  //   // Basic check for required fields (must be a positive number)
  //   if (
  //     !basePrice ||
  //     !costPrice ||
  //     !sellingPrice ||
  //     !mrp ||
  //     basePrice <= 0 ||
  //     costPrice <= 0 ||
  //     sellingPrice <= 0 ||
  //     mrp <= 0
  //   ) {
  //     this.toastr.warning(
  //       'All pricing fields must be positive values.',
  //       'Validation Error'
  //     );
  //     return false;
  //   }

  //   // Enforce the rule: Base Price < Cost Price <= Selling Price <= MRP
  //   if (basePrice >= costPrice) {
  //     this.toastr.error(
  //       'Base Price must be strictly less than Cost Price.',
  //       'Validation Error'
  //     );
  //     return false;
  //   }

  //   if (costPrice > sellingPrice) {
  //     this.toastr.error(
  //       'Cost Price must be less than or equal to Selling Price.',
  //       'Validation Error'
  //     );
  //     return false;
  //   }

  //   if (sellingPrice > mrp) {
  //     this.toastr.error(
  //       'Selling Price must be less than or equal to MRP.',
  //       'Validation Error'
  //     );
  //     return false;
  //   }

  //   return true;
  // }
  validatePricing(): boolean {
    this.newPricingValidationError = this.getPricingValidationError();
    return this.newPricingValidationError === null;
  }

  /**
   * Selling at cost is allowed, but it is nearly always worth a second look,
   * so the popup flags it without blocking the save.
   */
  get isZeroMarginPricing(): boolean {
    const cp = Number(this.newPricing?.costPrice);
    const sp = Number(this.newPricing?.sellingPrice);
    return cp > 0 && sp > 0 && cp === sp;
  }

  /** Round to 3 decimals so derived prices (base × (1+tax)) compare cleanly. */
  private round3(v: number): number {
    return Math.round(v * 1000) / 1000;
  }

  getPricingValidationError(): string | null {
    if (
      !this.newPricing.basePrice ||
      !this.newPricing.costPrice ||
      !this.newPricing.sellingPrice ||
      !this.newPricing.mrp ||
      this.newPricing.basePrice <= 0 ||
      this.newPricing.costPrice <= 0 ||
      this.newPricing.sellingPrice <= 0 ||
      this.newPricing.mrp <= 0
    ) {
      return 'All pricing fields must be positive values.';
    }

    const basePrice = this.round3(Number(this.newPricing.basePrice));
    const costPrice = this.round3(Number(this.newPricing.costPrice));
    const sellingPrice = this.round3(Number(this.newPricing.sellingPrice));
    const mrp = this.round3(Number(this.newPricing.mrp));

    if (basePrice > costPrice) {
      return 'Base Price cannot be greater than Cost Price.';
    }

    if (mrp < costPrice) {
      return 'MRP should always be greater than or equal to Cost Price.';
    }

    if (sellingPrice < costPrice) {
      return 'Selling Price should also be greater than or equal to Cost Price.';
    }

    if (sellingPrice > mrp) {
      return 'Selling Price can be same as MRP but not greater.';
    }

    return null;
  }

  private hasOverReceipt(): boolean {
    return this.grnItems.some((item) => {
      const received = Number(item.receivedQuantity || 0);
      const po = Number(item.poQuantity || 0);
      return received > po;
    });
  }
  getCurrentProductGstRate(): number {
    const gstPercent = parseFloat(this.selectedProduct?.gstPercent || this.selectedProduct?.purchaseGstRate?.value || '0');
    console.log(`[GRN Tax Info] Selected Product GST: ${gstPercent}%`);
    // Return the rate as a factor (e.g., 5% becomes 0.05)
    return gstPercent / 100;
  }

  // ** NEW METHOD FOR MODAL PRICING CALCULATION **
  calculateNewPricing(changedField: 'base' | 'cost' | 'mrp' | 'sellingPrice'): void {
    const p = this.selectedProduct;
    // Margin-driven CP is opt-in via the popup checkbox; when it is off the
    // margin is treated as 0 so MRP and CP stay independently editable.
    const marginPercent = this.useMarginForCp
      ? Number(this.pricingMarginPercent) || 0
      : 0;
    const gstRateFactor = this.getCurrentProductGstRate();
    const cessRate = parseFloat(p?.cessRate || '0') / 100;
    const taxFactor = gstRateFactor + cessRate;

    console.log(`[GRN Pricing Calc] Trigger: ${changedField}, Margin: ${marginPercent}%, Tax Factor: ${taxFactor.toFixed(4)}`);

    // Coerce to number, treat null/undefined/empty string as 0
    let basePrice = Number(this.newPricing.basePrice) || 0;
    let costPrice = Number(this.newPricing.costPrice) || 0;
    let mrp = Number(this.newPricing.mrp) || 0;

    // MASTER-DRIVER LOGIC:
    // 1. If MRP is edited with margin enabled, it calculates Cost Price and Base Price.
    // 2. If Margin is off/0, we skip the CP/MRP cross-calculation to allow independent entry.
    // 3. If Cost/Base Price is edited, it only updates MRP if MRP is 0 OR if CP > MRP.

    if (changedField === 'mrp') {
      // From MRP: CP = MRP * (1 - Margin%), Base = CP / (1 + Tax)
      if (marginPercent > 0) {
        costPrice = mrp * (1 - marginPercent / 100);
        basePrice = costPrice / (1 + taxFactor);
      }
    } else if (changedField === 'cost') {
      // From CP: Base = CP / (1 + Tax)
      basePrice = costPrice / (1 + taxFactor);

      // Update MRP only if it's currently empty/zero OR if we just exceeded it
      if (marginPercent > 0 && (mrp === 0 || costPrice > mrp)) {
        mrp = costPrice / (1 - marginPercent / 100);
      }
    } else if (changedField === 'base') {
      // From Base Price: CP = Base * (1 + Tax)
      costPrice = basePrice * (1 + taxFactor);

      // Update MRP only if it's currently empty/zero OR if we just exceeded it
      if (marginPercent > 0 && (mrp === 0 || costPrice > mrp)) {
        mrp = costPrice / (1 - marginPercent / 100);
      }
    }

    if (changedField === 'sellingPrice') {
      // Only the SP input changed: nothing to derive, and a typed SP stops
      // following MRP.
      this.sellingFollowsMrp = false;
      this.validatePricing();
      return;
    }

    this.newPricing.basePrice = parseFloat(basePrice.toFixed(4));
    this.newPricing.costPrice = parseFloat(costPrice.toFixed(4));
    this.newPricing.mrp = parseFloat(mrp.toFixed(4));

    // Selling Price is entered by the user, never clamped to MRP. The old
    // rule clamped SP down whenever SP > MRP, which fired on every MRP
    // keystroke: with SP = 400, typing "4" of "450" rewrote SP to 4 and it
    // never recovered. SP > MRP is left to validation to report. The one
    // exception: the user agreed to sell at MRP (offerSellingAtMrp).
    if (this.sellingFollowsMrp) {
      this.newPricing.sellingPrice = this.newPricing.mrp;
    }

    this.validatePricing();
  }

  /** MRP field (change): offer SP = MRP while the selling price is still empty. */
  async offerSellingAtMrp(): Promise<void> {
    const mrp = Number(this.newPricing.mrp) || 0;
    if (this.sellingAtMrpAsked || mrp <= 0 || Number(this.newPricing.sellingPrice) > 0) return;
    this.sellingAtMrpAsked = true;
    const yes = await this.confirmDialog.confirm({
      title: 'Selling price same as MRP?',
      message: `Set the selling price to the MRP, ₹${mrp.toFixed(2)}? Choose No to enter a different selling price.`,
      confirmText: 'Yes, sell at MRP',
      cancelText: 'No',
      tone: 'neutral',
      icon: 'bi-tag',
    });
    if (!yes || !this.isPricingFormVisible) return;
    this.sellingFollowsMrp = true;
    this.newPricing.sellingPrice = Number(this.newPricing.mrp) || 0;
    this.validatePricing();
  }

  /**
   * Margin hints shown under the price fields, each only once both inputs it
   * needs are positive.
   *   Vendor Margin % (under MRP)            = (MRP − CP) / MRP × 100
   *   Customer Profit Margin % (under SP)    = (SP − CP) / SP × 100
   */
  get vendorMarginPercent(): number | null {
    const cp = Number(this.newPricing?.costPrice) || 0;
    const mrp = Number(this.newPricing?.mrp) || 0;
    if (cp <= 0 || mrp <= 0) return null;
    return ((mrp - cp) / mrp) * 100;
  }

  get customerMarginPercent(): number | null {
    const cp = Number(this.newPricing?.costPrice) || 0;
    const sp = Number(this.newPricing?.sellingPrice) || 0;
    if (cp <= 0 || sp <= 0) return null;
    return ((sp - cp) / sp) * 100;
  }

  // 🛡️ CENTRAL SANITIZER: Enforce Basic Pricing Integrity (Enforces CP <= SP <= MRP)
  private sanitizeItemPricing(item: any) {
    if (!item) return;

    const cp = this.round3(Number(item.costPrice || 0));
    const margin = Number(item.marginPercentage || 10);
    const safetyMargin = margin > 0 ? margin : 10;

    // 1. MRP must be >= CP
    // Selling at cost is a real scenario, so MRP == CP is left alone; only a
    // genuinely below-cost MRP falls back to the margin-based calculation.
    if (this.round3(Number(item.mrp || 0)) < cp) {
      item.mrp = Number((cp / (1 - safetyMargin / 100)).toFixed(2));
      console.warn(
        `[PRICING SANITIZER] MRP (₹${item.mrp}) was < CP (₹${cp}) for ${item.skuCode}. Reset to ${safetyMargin}% margin.`,
      );
    }

    // 2. Selling Price must be >= CP
    // SP == CP (zero margin) is preserved. This used to be `<=`, which
    // silently replaced a deliberate at-cost price with the MRP, so a
    // zero-margin line could never actually be saved from the GRN screen.
    if (this.round3(Number(item.sellingPrice || 0)) < cp) {
      item.sellingPrice = item.mrp;
      console.warn(
        `[PRICING SANITIZER] Selling Price (₹${item.sellingPrice}) was < CP (₹${cp}) for ${item.skuCode}. Reset to MRP.`,
      );
    }

    // 3. Selling Price must be <= MRP
    // Previous "SP > MRP" check logic, unified here
    if (this.round3(Number(item.sellingPrice || 0)) > this.round3(Number(item.mrp || 0))) {
      item.sellingPrice = item.mrp;
      console.warn(
        `[PRICING SANITIZER] Selling Price (₹${item.sellingPrice}) exceeded MRP (₹${item.mrp}) for ${item.skuCode}. Capped at MRP.`,
      );
    }
  }
}
